import { useState, useEffect } from 'react';
import { useDebounce } from '../../hooks/useDebounce';
import { formatMoney } from '../../utils/format';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { CreditCard, Building2, Search, RefreshCcw, Plus, Trash2, Star, Loader2, AlertCircle, CheckCircle, Wallet, History, DollarSign, ArrowRight, ArrowRightLeft, X, Landmark, Download, Upload, Link2, Edit2 } from 'lucide-react';
import { adminClientPaymentsAPI, adminCompanyAPI, depositsAPI } from '../../services/api';
import { Link } from 'react-router-dom';
import ApplyCreditModal from '../../components/admin/ApplyCreditModal';
import { Banknote } from 'lucide-react';
import { usePaymentGateway, isHostedGateway, HostedAddMethod } from '../../components/payments/CardForm';
import RefundPaymentModal from '../../components/admin/RefundPaymentModal';
import AutopayPreviewPanel from '../../components/admin/AutopayPreviewPanel';

/*
 * Adding a card or bank account for a client, on whichever gateway this
 * portal uses: the portal's own form (Portal*) on Authorize.net, the
 * gateway's secure form on the hosted one (only the confirmed id is posted).
 */
function AddCardModal(props) {
  const gw = usePaymentGateway();
  if (!isHostedGateway(gw.gateway)) return <PortalAddCardModal {...props} />;
  return (
    <HostedAddMethod
      methodType="card"
      publishableKey={gw.publishableKey}
      stripeAccount={gw.stripeAccount}
      begin={() => adminClientPaymentsAPI.beginSavedMethod(props.companyId, 'card')}
      save={(data) => adminClientPaymentsAPI.addCard(props.companyId, data)}
      onSuccess={() => props.onSuccess()}
      onCancel={props.onClose}
    />
  );
}

function AddBankModal(props) {
  const gw = usePaymentGateway();
  if (!isHostedGateway(gw.gateway)) return <PortalAddBankModal {...props} />;
  return (
    <HostedAddMethod
      methodType="ach"
      publishableKey={gw.publishableKey}
      stripeAccount={gw.stripeAccount}
      begin={() => adminClientPaymentsAPI.beginSavedMethod(props.companyId, 'ach')}
      save={(data) => adminClientPaymentsAPI.addBank(props.companyId, data)}
      onSuccess={() => props.onSuccess()}
      onCancel={props.onClose}
    />
  );
}

const TABS = [
  { id: 'methods', label: 'Payment Methods', icon: CreditCard },
  { id: 'recurring', label: 'Recurring', icon: RefreshCcw },
  { id: 'autopay', label: 'AutoPay', icon: Wallet },
  { id: 'deposits', label: 'Deposits', icon: Banknote },
  { id: 'credits', label: 'Credits', icon: DollarSign },
  { id: 'history', label: 'History', icon: History },
];

export default function AdminClientPaymentsPage() {
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState('methods');
  const [selectedCompanyId, setSelectedCompanyId] = useState(null);
  const [companySearch, setCompanySearch] = useState('');
  const [showImportModal, setShowImportModal] = useState(false);
  const [showCompanyDropdown, setShowCompanyDropdown] = useState(false);
  const debouncedCompanySearch = useDebounce(companySearch, 300);

  // Search companies
  const { data: companiesData, isLoading: companiesLoading } = useQuery({
    queryKey: ['admin-companies-search', debouncedCompanySearch],
    queryFn: () => adminCompanyAPI.list({ search: debouncedCompanySearch, limit: 10 }),
    enabled: debouncedCompanySearch.length >= 2,
  });

  const companies = companiesData?.data?.companies || [];

  // Handle company selection
  const handleSelectCompany = (company) => {
    setSelectedCompanyId(company.cw_company_id || company.id);
    setCompanySearch(company.name);
    setShowCompanyDropdown(false);
  };

  return (
    <div className="max-w-6xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-3">
            <CreditCard className="w-6 h-6 text-neutral-400" />
            <h1 className="text-2xl font-bold text-neutral-900 dark:text-white">
              Client Payment Management
            </h1>
          </div>
          <button
            onClick={() => setShowImportModal(true)}
            className="flex items-center gap-2 px-3 py-2 text-sm bg-neutral-100 hover:bg-neutral-200 dark:bg-neutral-700 dark:hover:bg-neutral-600 text-neutral-700 dark:text-neutral-200 rounded-lg transition-colors"
          >
            <Download className="w-4 h-4" />
            Import ARB Subscriptions
          </button>
        </div>
        <p className="text-neutral-500 dark:text-neutral-400">
          Manage payment methods, recurring payments, and credits for clients
        </p>
      </div>

      {/* Company Selector */}
      <div className="bg-white dark:bg-neutral-800 rounded-lg border border-neutral-200 dark:border-neutral-700 p-4 mb-6">
        <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
          Select Company
        </label>
        <div className="relative">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400" />
            <input
              type="text"
              value={companySearch}
              onChange={(e) => {
                setCompanySearch(e.target.value);
                setShowCompanyDropdown(true);
                if (e.target.value.length < 2) {
                  setSelectedCompanyId(null);
                }
              }}
              onFocus={() => setShowCompanyDropdown(true)}
              placeholder="Search for a company..."
              className="w-full pl-10 pr-4 py-2 border border-neutral-300 dark:border-neutral-600 bg-white dark:bg-neutral-700 text-neutral-900 dark:text-white rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            />
          </div>

          {/* Company dropdown */}
          {showCompanyDropdown && companySearch.length >= 2 && (
            <div className="absolute z-10 w-full mt-1 bg-white dark:bg-neutral-800 border border-neutral-200 dark:border-neutral-700 rounded-lg shadow-lg max-h-60 overflow-auto">
              {companiesLoading ? (
                <div className="p-3 text-center text-neutral-500">
                  <Loader2 className="w-4 h-4 animate-spin inline mr-2" />
                  Searching...
                </div>
              ) : companies.length > 0 ? (
                companies.map((company) => (
                  <button
                    key={company.id}
                    onClick={() => handleSelectCompany(company)}
                    className="w-full px-4 py-2 text-left hover:bg-neutral-100 dark:hover:bg-neutral-700 flex items-center gap-2"
                  >
                    <Building2 className="w-4 h-4 text-neutral-400" />
                    <div>
                      <div className="text-sm font-medium text-neutral-900 dark:text-white">
                        {company.name}
                      </div>
                      <div className="text-xs text-neutral-500">
                        ID: {company.cw_company_id || company.id}
                      </div>
                    </div>
                  </button>
                ))
              ) : (
                <div className="p-3 text-center text-neutral-500">No companies found</div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* AutoPay charging across every company (Phase 7e) */}
      <div className="mb-6">
        <AutopayPreviewPanel collapsible />
      </div>

      {/* Main Content */}
      {selectedCompanyId ? (
        <>
          {/* Tabs */}
          <div className="border-b border-neutral-200 dark:border-neutral-700 mb-6">
            <nav className="flex gap-4">
              {TABS.map((tab) => {
                const Icon = tab.icon;
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 -mb-px transition-colors ${
                      activeTab === tab.id
                        ? 'border-primary-500 text-primary-600 dark:text-primary-400'
                        : 'border-transparent text-neutral-500 hover:text-neutral-700 dark:hover:text-neutral-300'
                    }`}
                  >
                    <Icon className="w-4 h-4" />
                    {tab.label}
                  </button>
                );
              })}
            </nav>
          </div>

          {/* Tab Content */}
          {activeTab === 'methods' && <PaymentMethodsTab companyId={selectedCompanyId} />}
          {activeTab === 'recurring' && <RecurringTab companyId={selectedCompanyId} />}
          {activeTab === 'autopay' && <AutoPayTab companyId={selectedCompanyId} />}
          {activeTab === 'deposits' && <DepositsTab companyId={selectedCompanyId} />}
          {activeTab === 'credits' && <CreditsTab companyId={selectedCompanyId} />}
          {activeTab === 'history' && <HistoryTab companyId={selectedCompanyId} />}
        </>
      ) : (
        <div className="bg-white dark:bg-neutral-800 rounded-lg border border-neutral-200 dark:border-neutral-700 p-12 text-center">
          <Building2 className="w-12 h-12 text-neutral-300 dark:text-neutral-600 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-neutral-900 dark:text-white mb-2">
            Select a Company
          </h3>
          <p className="text-neutral-500 dark:text-neutral-400">
            Search and select a company above to manage their payment settings
          </p>
        </div>
      )}

      {/* Import ARB Modal */}
      {showImportModal && (
        <ImportARBModal
          onClose={() => setShowImportModal(false)}
          onImportComplete={() => {
            setShowImportModal(false);
            // Refresh data if a company is selected
            if (selectedCompanyId) {
              queryClient.invalidateQueries({ queryKey: ['admin-recurring-payments', selectedCompanyId] });
            }
          }}
        />
      )}
    </div>
  );
}

// ==========================================
// PAYMENT METHODS TAB
// ==========================================

function PaymentMethodsTab({ companyId }) {
  const queryClient = useQueryClient();
  const [showAddCardModal, setShowAddCardModal] = useState(false);
  const [showAddBankModal, setShowAddBankModal] = useState(false);
  const [editingMethod, setEditingMethod] = useState(null);

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['admin-payment-methods', companyId],
    queryFn: () => adminClientPaymentsAPI.getPaymentMethods(companyId),
    enabled: !!companyId,
  });

  const paymentMethods = data?.data?.paymentMethods || [];
  const company = data?.data?.company;

  const deleteMutation = useMutation({
    mutationFn: (methodId) => adminClientPaymentsAPI.deletePaymentMethod(companyId, methodId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-payment-methods', companyId] });
    },
  });

  const setDefaultMutation = useMutation({
    mutationFn: (methodId) => adminClientPaymentsAPI.setDefaultPaymentMethod(companyId, methodId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-payment-methods', companyId] });
    },
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary-500" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-lg p-4 flex items-center gap-2">
        <AlertCircle className="w-5 h-5 text-red-500" />
        <span className="text-red-700 dark:text-red-300">Failed to load payment methods</span>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Company Info */}
      {company && (
        <div className="bg-neutral-50 dark:bg-neutral-800/50 rounded-lg p-3 flex items-center gap-2 text-sm">
          <Building2 className="w-4 h-4 text-neutral-400" />
          <span className="text-neutral-600 dark:text-neutral-300">
            {company.name} ({company.identifier})
          </span>
        </div>
      )}

      {/* Payment Methods List */}
      <div className="bg-white dark:bg-neutral-800 rounded-lg border border-neutral-200 dark:border-neutral-700">
        <div className="px-4 py-3 border-b border-neutral-200 dark:border-neutral-700 flex items-center justify-between">
          <h3 className="font-medium text-neutral-900 dark:text-white">Saved Payment Methods</h3>
          <div className="flex gap-2">
            <button
              onClick={() => setShowAddCardModal(true)}
              className="flex items-center gap-1 px-3 py-1.5 text-sm bg-primary-600 text-white rounded-lg hover:bg-primary-700"
            >
              <Plus className="w-4 h-4" />
              Add Card
            </button>
            <button
              onClick={() => setShowAddBankModal(true)}
              className="flex items-center gap-1 px-3 py-1.5 text-sm bg-neutral-100 dark:bg-neutral-700 text-neutral-700 dark:text-neutral-300 rounded-lg hover:bg-neutral-200 dark:hover:bg-neutral-600"
            >
              <Plus className="w-4 h-4" />
              Add Bank
            </button>
          </div>
        </div>

        {paymentMethods.length === 0 ? (
          <div className="p-8 text-center text-neutral-500">
            <CreditCard className="w-12 h-12 text-neutral-300 mx-auto mb-3" />
            <p>No payment methods on file</p>
          </div>
        ) : (
          <div className="divide-y divide-neutral-200 dark:divide-neutral-700">
            {paymentMethods.map((method) => (
              <div key={method.id} className="p-4 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  {method.payment_type === 'card' ? (
                    <CreditCard className="w-8 h-8 text-neutral-400" />
                  ) : (
                    <Landmark className="w-8 h-8 text-neutral-400" />
                  )}
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-neutral-900 dark:text-white">
                        {method.payment_type === 'card'
                          ? `${method.card_type?.toUpperCase() || 'Card'} ****${method.last_four}`
                          : `${method.bank_name || 'Bank'} ****${method.account_last_four}`}
                      </span>
                      {method.is_default && (
                        <span className="px-2 py-0.5 text-xs bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300 rounded-full">
                          Default
                        </span>
                      )}
                    </div>
                    <div className="text-sm text-neutral-500">
                      {method.payment_type === 'card'
                        ? `Expires ${method.exp_month}/${method.exp_year}`
                        : `${method.account_type} account`}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setEditingMethod(method)}
                    className="p-2 text-neutral-400 hover:text-primary-500 transition-colors"
                    title="Edit"
                  >
                    <Edit2 className="w-4 h-4" />
                  </button>
                  {!method.is_default && (
                    <button
                      onClick={() => setDefaultMutation.mutate(method.id)}
                      disabled={setDefaultMutation.isPending}
                      className="p-2 text-neutral-400 hover:text-primary-500 transition-colors"
                      title="Set as default"
                    >
                      <Star className="w-4 h-4" />
                    </button>
                  )}
                  <button
                    onClick={() => {
                      if (confirm('Are you sure you want to delete this payment method?')) {
                        deleteMutation.mutate(method.id);
                      }
                    }}
                    disabled={deleteMutation.isPending}
                    className="p-2 text-neutral-400 hover:text-red-500 transition-colors"
                    title="Delete"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Add Card Modal */}
      {showAddCardModal && (
        <AddCardModal
          companyId={companyId}
          onClose={() => setShowAddCardModal(false)}
          onSuccess={() => {
            setShowAddCardModal(false);
            refetch();
          }}
        />
      )}

      {/* Add Bank Modal */}
      {showAddBankModal && (
        <AddBankModal
          companyId={companyId}
          onClose={() => setShowAddBankModal(false)}
          onSuccess={() => {
            setShowAddBankModal(false);
            refetch();
          }}
        />
      )}

      {/* Edit Payment Method Modal */}
      {editingMethod && (
        <EditPaymentMethodModal
          companyId={companyId}
          method={editingMethod}
          onClose={() => setEditingMethod(null)}
          onSuccess={() => {
            setEditingMethod(null);
            refetch();
          }}
        />
      )}
    </div>
  );
}

// ==========================================
// EDIT PAYMENT METHOD MODAL
// ==========================================

function EditPaymentMethodModal({ companyId, method, onClose, onSuccess }) {
  const isCard = method.payment_type === 'card';

  // Parse existing billing address
  const existingBillingAddress = typeof method.billing_address === 'string'
    ? JSON.parse(method.billing_address)
    : method.billing_address || {};

  const [formData, setFormData] = useState({
    // Card fields
    expirationMonth: method.expiration_month?.toString() || '',
    expirationYear: method.expiration_year?.toString() || '',
    // Bank fields
    routingNumber: '',
    accountNumber: '',
    accountType: method.account_type || 'checking',
    nameOnAccount: method.name_on_account || '',
    bankName: method.bank_name || '',
    // Common
    billingAddress: {
      firstName: existingBillingAddress.firstName || '',
      lastName: existingBillingAddress.lastName || '',
      address: existingBillingAddress.address || '',
      city: existingBillingAddress.city || '',
      state: existingBillingAddress.state || '',
      zip: existingBillingAddress.zip || '',
    },
  });
  const [error, setError] = useState(null);

  const mutation = useMutation({
    mutationFn: (data) => adminClientPaymentsAPI.updatePaymentMethod(companyId, method.id, data),
    onSuccess: () => onSuccess(),
    onError: (err) => setError(err.response?.data?.error || 'Failed to update payment method'),
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    setError(null);

    if (isCard) {
      mutation.mutate({
        expirationMonth: parseInt(formData.expirationMonth),
        expirationYear: parseInt(formData.expirationYear),
        billingAddress: formData.billingAddress,
      });
    } else {
      if (!formData.routingNumber || !formData.accountNumber) {
        setError('Routing number and account number are required for bank accounts');
        return;
      }
      mutation.mutate({
        routingNumber: formData.routingNumber,
        accountNumber: formData.accountNumber,
        accountType: formData.accountType,
        nameOnAccount: formData.nameOnAccount,
        bankName: formData.bankName,
        billingAddress: formData.billingAddress,
      });
    }
  };

  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 15 }, (_, i) => currentYear + i);
  const months = Array.from({ length: 12 }, (_, i) => (i + 1).toString().padStart(2, '0'));

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-white dark:bg-neutral-800 rounded-lg w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="px-6 py-4 border-b border-neutral-200 dark:border-neutral-700 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-neutral-900 dark:text-white">
            Edit {isCard ? 'Credit Card' : 'Bank Account'}
          </h2>
          <button onClick={onClose} className="text-neutral-400 hover:text-neutral-600">
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {error && (
            <div className="p-3 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-lg text-red-700 dark:text-red-300 text-sm">
              {error}
            </div>
          )}

          {/* Current Method Info */}
          <div className="p-3 bg-neutral-50 dark:bg-neutral-700/50 rounded-lg">
            <div className="flex items-center gap-2 text-sm text-neutral-600 dark:text-neutral-300">
              {isCard ? <CreditCard className="w-4 h-4" /> : <Landmark className="w-4 h-4" />}
              <span className="font-medium">
                {isCard
                  ? `${method.card_type?.toUpperCase() || 'Card'} ****${method.last_four}`
                  : `${method.bank_name || 'Bank'} ****${method.account_last_four}`}
              </span>
            </div>
          </div>

          {isCard ? (
            /* Card Fields */
            <div>
              <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">
                Expiration Date
              </label>
              <div className="grid grid-cols-2 gap-2">
                <select
                  value={formData.expirationMonth}
                  onChange={(e) => setFormData({ ...formData, expirationMonth: e.target.value })}
                  className="px-3 py-2 border border-neutral-300 dark:border-neutral-600 bg-white dark:bg-neutral-700 rounded-lg text-neutral-900 dark:text-white"
                  required
                >
                  <option value="">Month</option>
                  {months.map((m) => (
                    <option key={m} value={m}>{m}</option>
                  ))}
                </select>
                <select
                  value={formData.expirationYear}
                  onChange={(e) => setFormData({ ...formData, expirationYear: e.target.value })}
                  className="px-3 py-2 border border-neutral-300 dark:border-neutral-600 bg-white dark:bg-neutral-700 rounded-lg text-neutral-900 dark:text-white"
                  required
                >
                  <option value="">Year</option>
                  {years.map((y) => (
                    <option key={y} value={y}>{y}</option>
                  ))}
                </select>
              </div>
              <p className="mt-1 text-xs text-neutral-500">
                Note: Card number cannot be changed. Delete and re-add if needed.
              </p>
            </div>
          ) : (
            /* Bank Fields */
            <>
              <div className="p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700 rounded-lg">
                <p className="text-sm text-amber-700 dark:text-amber-300">
                  Enter the full routing and account numbers to update.
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">
                  Routing Number
                </label>
                <input
                  type="text"
                  value={formData.routingNumber}
                  onChange={(e) => setFormData({ ...formData, routingNumber: e.target.value.replace(/\D/g, '').slice(0, 9) })}
                  placeholder="9 digits"
                  className="w-full px-3 py-2 border border-neutral-300 dark:border-neutral-600 bg-white dark:bg-neutral-700 rounded-lg text-neutral-900 dark:text-white"
                  required
                  maxLength={9}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">
                  Account Number
                </label>
                <input
                  type="text"
                  value={formData.accountNumber}
                  onChange={(e) => setFormData({ ...formData, accountNumber: e.target.value.replace(/\D/g, '').slice(0, 17) })}
                  placeholder="Account number"
                  className="w-full px-3 py-2 border border-neutral-300 dark:border-neutral-600 bg-white dark:bg-neutral-700 rounded-lg text-neutral-900 dark:text-white"
                  required
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">
                    Account Type
                  </label>
                  <select
                    value={formData.accountType}
                    onChange={(e) => setFormData({ ...formData, accountType: e.target.value })}
                    className="w-full px-3 py-2 border border-neutral-300 dark:border-neutral-600 bg-white dark:bg-neutral-700 rounded-lg text-neutral-900 dark:text-white"
                  >
                    <option value="checking">Checking</option>
                    <option value="savings">Savings</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">
                    Name on Account
                  </label>
                  <input
                    type="text"
                    value={formData.nameOnAccount}
                    onChange={(e) => setFormData({ ...formData, nameOnAccount: e.target.value })}
                    className="w-full px-3 py-2 border border-neutral-300 dark:border-neutral-600 bg-white dark:bg-neutral-700 rounded-lg text-neutral-900 dark:text-white"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">
                  Bank Name
                </label>
                <input
                  type="text"
                  value={formData.bankName}
                  onChange={(e) => setFormData({ ...formData, bankName: e.target.value })}
                  className="w-full px-3 py-2 border border-neutral-300 dark:border-neutral-600 bg-white dark:bg-neutral-700 rounded-lg text-neutral-900 dark:text-white"
                />
              </div>
            </>
          )}

          {/* Billing Address */}
          <div className="border-t border-neutral-200 dark:border-neutral-700 pt-4">
            <h3 className="text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-3">Billing Address</h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-xs text-neutral-500 mb-1">First Name</label>
                <input
                  type="text"
                  value={formData.billingAddress.firstName}
                  onChange={(e) => setFormData({
                    ...formData,
                    billingAddress: { ...formData.billingAddress, firstName: e.target.value }
                  })}
                  className="w-full px-3 py-2 border border-neutral-300 dark:border-neutral-600 bg-white dark:bg-neutral-700 rounded-lg text-neutral-900 dark:text-white text-sm"
                  required
                />
              </div>
              <div>
                <label className="block text-xs text-neutral-500 mb-1">Last Name</label>
                <input
                  type="text"
                  value={formData.billingAddress.lastName}
                  onChange={(e) => setFormData({
                    ...formData,
                    billingAddress: { ...formData.billingAddress, lastName: e.target.value }
                  })}
                  className="w-full px-3 py-2 border border-neutral-300 dark:border-neutral-600 bg-white dark:bg-neutral-700 rounded-lg text-neutral-900 dark:text-white text-sm"
                  required
                />
              </div>
            </div>
            <div className="mt-3">
              <label className="block text-xs text-neutral-500 mb-1">Address</label>
              <input
                type="text"
                value={formData.billingAddress.address}
                onChange={(e) => setFormData({
                  ...formData,
                  billingAddress: { ...formData.billingAddress, address: e.target.value }
                })}
                className="w-full px-3 py-2 border border-neutral-300 dark:border-neutral-600 bg-white dark:bg-neutral-700 rounded-lg text-neutral-900 dark:text-white text-sm"
                required
              />
            </div>
            <div className="grid grid-cols-3 gap-2 mt-3">
              <div>
                <label className="block text-xs text-neutral-500 mb-1">City</label>
                <input
                  type="text"
                  value={formData.billingAddress.city}
                  onChange={(e) => setFormData({
                    ...formData,
                    billingAddress: { ...formData.billingAddress, city: e.target.value }
                  })}
                  className="w-full px-3 py-2 border border-neutral-300 dark:border-neutral-600 bg-white dark:bg-neutral-700 rounded-lg text-neutral-900 dark:text-white text-sm"
                  required
                />
              </div>
              <div>
                <label className="block text-xs text-neutral-500 mb-1">State</label>
                <input
                  type="text"
                  value={formData.billingAddress.state}
                  onChange={(e) => setFormData({
                    ...formData,
                    billingAddress: { ...formData.billingAddress, state: e.target.value }
                  })}
                  className="w-full px-3 py-2 border border-neutral-300 dark:border-neutral-600 bg-white dark:bg-neutral-700 rounded-lg text-neutral-900 dark:text-white text-sm"
                  required
                />
              </div>
              <div>
                <label className="block text-xs text-neutral-500 mb-1">ZIP</label>
                <input
                  type="text"
                  value={formData.billingAddress.zip}
                  onChange={(e) => setFormData({
                    ...formData,
                    billingAddress: { ...formData.billingAddress, zip: e.target.value }
                  })}
                  className="w-full px-3 py-2 border border-neutral-300 dark:border-neutral-600 bg-white dark:bg-neutral-700 rounded-lg text-neutral-900 dark:text-white text-sm"
                  required
                />
              </div>
            </div>
          </div>

          <div className="flex justify-end gap-3 pt-4 border-t border-neutral-200 dark:border-neutral-700">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-neutral-600 dark:text-neutral-400 hover:text-neutral-800 dark:hover:text-neutral-200"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={mutation.isPending}
              className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 flex items-center gap-2"
            >
              {mutation.isPending && <Loader2 className="w-4 h-4 animate-spin" />}
              Save Changes
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ==========================================
// ADD CARD MODAL
// ==========================================

function PortalAddCardModal({ companyId, onClose, onSuccess }) {
  const [formData, setFormData] = useState({
    cardNumber: '',
    expirationMonth: '',
    expirationYear: '',
    cvv: '',
    billingAddress: {
      firstName: '',
      lastName: '',
      address: '',
      city: '',
      state: '',
      zip: '',
    },
    isDefault: false,
  });
  const [error, setError] = useState(null);

  const mutation = useMutation({
    mutationFn: (data) => adminClientPaymentsAPI.addCard(companyId, data),
    onSuccess: () => onSuccess(),
    onError: (err) => setError(err.response?.data?.error || 'Failed to add card'),
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    setError(null);
    mutation.mutate({
      ...formData,
      expirationMonth: parseInt(formData.expirationMonth),
      expirationYear: parseInt(formData.expirationYear),
    });
  };

  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 15 }, (_, i) => currentYear + i);
  const months = Array.from({ length: 12 }, (_, i) => (i + 1).toString().padStart(2, '0'));

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-white dark:bg-neutral-800 rounded-lg w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="px-6 py-4 border-b border-neutral-200 dark:border-neutral-700 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-neutral-900 dark:text-white">Add Credit Card</h2>
          <button onClick={onClose} className="text-neutral-400 hover:text-neutral-600">
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {error && (
            <div className="p-3 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-lg text-red-700 dark:text-red-300 text-sm">
              {error}
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">
              Card Number
            </label>
            <input
              type="text"
              value={formData.cardNumber}
              onChange={(e) => setFormData({ ...formData, cardNumber: e.target.value.replace(/\D/g, '') })}
              maxLength={19}
              placeholder="4111111111111111"
              className="w-full px-3 py-2 border border-neutral-300 dark:border-neutral-600 bg-white dark:bg-neutral-700 rounded-lg"
              required
            />
          </div>

          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">
                Month
              </label>
              <select
                value={formData.expirationMonth}
                onChange={(e) => setFormData({ ...formData, expirationMonth: e.target.value })}
                className="w-full px-3 py-2 border border-neutral-300 dark:border-neutral-600 bg-white dark:bg-neutral-700 rounded-lg"
                required
              >
                <option value="">MM</option>
                {months.map((m) => (
                  <option key={m} value={m}>{m}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">
                Year
              </label>
              <select
                value={formData.expirationYear}
                onChange={(e) => setFormData({ ...formData, expirationYear: e.target.value })}
                className="w-full px-3 py-2 border border-neutral-300 dark:border-neutral-600 bg-white dark:bg-neutral-700 rounded-lg"
                required
              >
                <option value="">YYYY</option>
                {years.map((y) => (
                  <option key={y} value={y}>{y}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">
                CVV
              </label>
              <input
                type="text"
                value={formData.cvv}
                onChange={(e) => setFormData({ ...formData, cvv: e.target.value.replace(/\D/g, '') })}
                maxLength={4}
                placeholder="123"
                className="w-full px-3 py-2 border border-neutral-300 dark:border-neutral-600 bg-white dark:bg-neutral-700 rounded-lg"
                required
              />
            </div>
          </div>

          <div className="border-t border-neutral-200 dark:border-neutral-700 pt-4">
            <h3 className="text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-3">
              Billing Address
            </h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm text-neutral-600 dark:text-neutral-400 mb-1">First Name</label>
                <input
                  type="text"
                  value={formData.billingAddress.firstName}
                  onChange={(e) => setFormData({
                    ...formData,
                    billingAddress: { ...formData.billingAddress, firstName: e.target.value }
                  })}
                  className="w-full px-3 py-2 border border-neutral-300 dark:border-neutral-600 bg-white dark:bg-neutral-700 rounded-lg"
                  required
                />
              </div>
              <div>
                <label className="block text-sm text-neutral-600 dark:text-neutral-400 mb-1">Last Name</label>
                <input
                  type="text"
                  value={formData.billingAddress.lastName}
                  onChange={(e) => setFormData({
                    ...formData,
                    billingAddress: { ...formData.billingAddress, lastName: e.target.value }
                  })}
                  className="w-full px-3 py-2 border border-neutral-300 dark:border-neutral-600 bg-white dark:bg-neutral-700 rounded-lg"
                  required
                />
              </div>
            </div>
            <div className="mt-3">
              <label className="block text-sm text-neutral-600 dark:text-neutral-400 mb-1">Address</label>
              <input
                type="text"
                value={formData.billingAddress.address}
                onChange={(e) => setFormData({
                  ...formData,
                  billingAddress: { ...formData.billingAddress, address: e.target.value }
                })}
                className="w-full px-3 py-2 border border-neutral-300 dark:border-neutral-600 bg-white dark:bg-neutral-700 rounded-lg"
                required
              />
            </div>
            <div className="grid grid-cols-3 gap-4 mt-3">
              <div>
                <label className="block text-sm text-neutral-600 dark:text-neutral-400 mb-1">City</label>
                <input
                  type="text"
                  value={formData.billingAddress.city}
                  onChange={(e) => setFormData({
                    ...formData,
                    billingAddress: { ...formData.billingAddress, city: e.target.value }
                  })}
                  className="w-full px-3 py-2 border border-neutral-300 dark:border-neutral-600 bg-white dark:bg-neutral-700 rounded-lg"
                  required
                />
              </div>
              <div>
                <label className="block text-sm text-neutral-600 dark:text-neutral-400 mb-1">State</label>
                <input
                  type="text"
                  value={formData.billingAddress.state}
                  onChange={(e) => setFormData({
                    ...formData,
                    billingAddress: { ...formData.billingAddress, state: e.target.value }
                  })}
                  maxLength={2}
                  placeholder="TX"
                  className="w-full px-3 py-2 border border-neutral-300 dark:border-neutral-600 bg-white dark:bg-neutral-700 rounded-lg"
                  required
                />
              </div>
              <div>
                <label className="block text-sm text-neutral-600 dark:text-neutral-400 mb-1">ZIP</label>
                <input
                  type="text"
                  value={formData.billingAddress.zip}
                  onChange={(e) => setFormData({
                    ...formData,
                    billingAddress: { ...formData.billingAddress, zip: e.target.value }
                  })}
                  className="w-full px-3 py-2 border border-neutral-300 dark:border-neutral-600 bg-white dark:bg-neutral-700 rounded-lg"
                  required
                />
              </div>
            </div>
          </div>

          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={formData.isDefault}
              onChange={(e) => setFormData({ ...formData, isDefault: e.target.checked })}
              className="rounded border-neutral-300"
            />
            <span className="text-sm text-neutral-700 dark:text-neutral-300">Set as default payment method</span>
          </label>

          <div className="flex justify-end gap-3 pt-4 border-t border-neutral-200 dark:border-neutral-700">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-neutral-600 dark:text-neutral-400 hover:text-neutral-800 dark:hover:text-neutral-200"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={mutation.isPending}
              className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 flex items-center gap-2"
            >
              {mutation.isPending && <Loader2 className="w-4 h-4 animate-spin" />}
              Add Card
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ==========================================
// ADD BANK MODAL
// ==========================================

function PortalAddBankModal({ companyId, onClose, onSuccess }) {
  const [formData, setFormData] = useState({
    accountNumber: '',
    routingNumber: '',
    accountType: 'checking',
    nameOnAccount: '',
    bankName: '',
    billingAddress: {
      firstName: '',
      lastName: '',
      address: '',
      city: '',
      state: '',
      zip: '',
    },
    isDefault: false,
  });
  const [error, setError] = useState(null);

  const mutation = useMutation({
    mutationFn: (data) => adminClientPaymentsAPI.addBank(companyId, data),
    onSuccess: () => onSuccess(),
    onError: (err) => setError(err.response?.data?.error || 'Failed to add bank account'),
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    setError(null);
    mutation.mutate(formData);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-white dark:bg-neutral-800 rounded-lg w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="px-6 py-4 border-b border-neutral-200 dark:border-neutral-700 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-neutral-900 dark:text-white">Add Bank Account</h2>
          <button onClick={onClose} className="text-neutral-400 hover:text-neutral-600">
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {error && (
            <div className="p-3 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-lg text-red-700 dark:text-red-300 text-sm">
              {error}
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">
              Bank Name
            </label>
            <input
              type="text"
              value={formData.bankName}
              onChange={(e) => setFormData({ ...formData, bankName: e.target.value })}
              placeholder="Bank of America"
              className="w-full px-3 py-2 border border-neutral-300 dark:border-neutral-600 bg-white dark:bg-neutral-700 rounded-lg"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">
              Name on Account
            </label>
            <input
              type="text"
              value={formData.nameOnAccount}
              onChange={(e) => setFormData({ ...formData, nameOnAccount: e.target.value })}
              className="w-full px-3 py-2 border border-neutral-300 dark:border-neutral-600 bg-white dark:bg-neutral-700 rounded-lg"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">
              Account Type
            </label>
            <select
              value={formData.accountType}
              onChange={(e) => setFormData({ ...formData, accountType: e.target.value })}
              className="w-full px-3 py-2 border border-neutral-300 dark:border-neutral-600 bg-white dark:bg-neutral-700 rounded-lg"
            >
              <option value="checking">Checking</option>
              <option value="savings">Savings</option>
            </select>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">
                Routing Number
              </label>
              <input
                type="text"
                value={formData.routingNumber}
                onChange={(e) => setFormData({ ...formData, routingNumber: e.target.value.replace(/\D/g, '') })}
                maxLength={9}
                placeholder="123456789"
                className="w-full px-3 py-2 border border-neutral-300 dark:border-neutral-600 bg-white dark:bg-neutral-700 rounded-lg"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">
                Account Number
              </label>
              <input
                type="text"
                value={formData.accountNumber}
                onChange={(e) => setFormData({ ...formData, accountNumber: e.target.value.replace(/\D/g, '') })}
                maxLength={17}
                className="w-full px-3 py-2 border border-neutral-300 dark:border-neutral-600 bg-white dark:bg-neutral-700 rounded-lg"
                required
              />
            </div>
          </div>

          <div className="border-t border-neutral-200 dark:border-neutral-700 pt-4">
            <h3 className="text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-3">
              Billing Address
            </h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm text-neutral-600 dark:text-neutral-400 mb-1">First Name</label>
                <input
                  type="text"
                  value={formData.billingAddress.firstName}
                  onChange={(e) => setFormData({
                    ...formData,
                    billingAddress: { ...formData.billingAddress, firstName: e.target.value }
                  })}
                  className="w-full px-3 py-2 border border-neutral-300 dark:border-neutral-600 bg-white dark:bg-neutral-700 rounded-lg"
                  required
                />
              </div>
              <div>
                <label className="block text-sm text-neutral-600 dark:text-neutral-400 mb-1">Last Name</label>
                <input
                  type="text"
                  value={formData.billingAddress.lastName}
                  onChange={(e) => setFormData({
                    ...formData,
                    billingAddress: { ...formData.billingAddress, lastName: e.target.value }
                  })}
                  className="w-full px-3 py-2 border border-neutral-300 dark:border-neutral-600 bg-white dark:bg-neutral-700 rounded-lg"
                  required
                />
              </div>
            </div>
            <div className="mt-3">
              <label className="block text-sm text-neutral-600 dark:text-neutral-400 mb-1">Address</label>
              <input
                type="text"
                value={formData.billingAddress.address}
                onChange={(e) => setFormData({
                  ...formData,
                  billingAddress: { ...formData.billingAddress, address: e.target.value }
                })}
                className="w-full px-3 py-2 border border-neutral-300 dark:border-neutral-600 bg-white dark:bg-neutral-700 rounded-lg"
                required
              />
            </div>
            <div className="grid grid-cols-3 gap-4 mt-3">
              <div>
                <label className="block text-sm text-neutral-600 dark:text-neutral-400 mb-1">City</label>
                <input
                  type="text"
                  value={formData.billingAddress.city}
                  onChange={(e) => setFormData({
                    ...formData,
                    billingAddress: { ...formData.billingAddress, city: e.target.value }
                  })}
                  className="w-full px-3 py-2 border border-neutral-300 dark:border-neutral-600 bg-white dark:bg-neutral-700 rounded-lg"
                  required
                />
              </div>
              <div>
                <label className="block text-sm text-neutral-600 dark:text-neutral-400 mb-1">State</label>
                <input
                  type="text"
                  value={formData.billingAddress.state}
                  onChange={(e) => setFormData({
                    ...formData,
                    billingAddress: { ...formData.billingAddress, state: e.target.value }
                  })}
                  maxLength={2}
                  className="w-full px-3 py-2 border border-neutral-300 dark:border-neutral-600 bg-white dark:bg-neutral-700 rounded-lg"
                  required
                />
              </div>
              <div>
                <label className="block text-sm text-neutral-600 dark:text-neutral-400 mb-1">ZIP</label>
                <input
                  type="text"
                  value={formData.billingAddress.zip}
                  onChange={(e) => setFormData({
                    ...formData,
                    billingAddress: { ...formData.billingAddress, zip: e.target.value }
                  })}
                  className="w-full px-3 py-2 border border-neutral-300 dark:border-neutral-600 bg-white dark:bg-neutral-700 rounded-lg"
                  required
                />
              </div>
            </div>
          </div>

          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={formData.isDefault}
              onChange={(e) => setFormData({ ...formData, isDefault: e.target.checked })}
              className="rounded border-neutral-300"
            />
            <span className="text-sm text-neutral-700 dark:text-neutral-300">Set as default payment method</span>
          </label>

          <div className="flex justify-end gap-3 pt-4 border-t border-neutral-200 dark:border-neutral-700">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-neutral-600 dark:text-neutral-400"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={mutation.isPending}
              className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 flex items-center gap-2"
            >
              {mutation.isPending && <Loader2 className="w-4 h-4 animate-spin" />}
              Add Bank Account
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ==========================================
// RECURRING TAB
// ==========================================

function RecurringTab({ companyId }) {
  const queryClient = useQueryClient();
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingPayment, setEditingPayment] = useState(null);

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['admin-recurring-payments', companyId],
    queryFn: () => adminClientPaymentsAPI.getRecurringPayments(companyId),
    enabled: !!companyId,
  });

  const { data: methodsData } = useQuery({
    queryKey: ['admin-payment-methods', companyId],
    queryFn: () => adminClientPaymentsAPI.getPaymentMethods(companyId),
    enabled: !!companyId,
  });

  const paymentMethods = methodsData?.data?.paymentMethods || [];
  const recurringPayments = data?.data?.recurringPayments || [];

  const cancelMutation = useMutation({
    mutationFn: (recurringId) => adminClientPaymentsAPI.cancelRecurringPayment(companyId, recurringId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-recurring-payments', companyId] });
    },
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary-500" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-lg p-4">
        <span className="text-red-700 dark:text-red-300">Failed to load recurring payments</span>
      </div>
    );
  }

  return (
    <div className="bg-white dark:bg-neutral-800 rounded-lg border border-neutral-200 dark:border-neutral-700">
      <div className="px-4 py-3 border-b border-neutral-200 dark:border-neutral-700 flex items-center justify-between">
        <h3 className="font-medium text-neutral-900 dark:text-white">Recurring Payments</h3>
        <button
          onClick={() => {
            if (paymentMethods.length === 0) {
              alert('Please add a payment method first before creating a recurring payment.');
              return;
            }
            setShowCreateModal(true);
          }}
          className="flex items-center gap-1 px-3 py-1.5 text-sm bg-primary-600 text-white rounded-lg hover:bg-primary-700"
        >
          <Plus className="w-4 h-4" />
          Create Recurring Payment
        </button>
      </div>

      {recurringPayments.length === 0 ? (
        <div className="p-8 text-center text-neutral-500">
          <RefreshCcw className="w-12 h-12 text-neutral-300 mx-auto mb-3" />
          <p>No recurring payments configured</p>
        </div>
      ) : (
        <div className="divide-y divide-neutral-200 dark:divide-neutral-700">
          {recurringPayments.map((payment) => (
            <div key={payment.id} className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-neutral-900 dark:text-white">
                      {payment.name}
                    </span>
                    {payment.source && (
                      <span className={`px-1.5 py-0.5 text-xs rounded ${
                        payment.source === 'imported'
                          ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/50 dark:text-blue-300'
                          : 'bg-purple-100 text-purple-700 dark:bg-purple-900/50 dark:text-purple-300'
                      }`}>
                        {payment.source === 'imported' ? 'Imported' : 'Portal'}
                      </span>
                    )}
                  </div>
                  <div className="text-sm text-neutral-500 mt-1">
                    ${parseFloat(payment.amount).toFixed(2)} / {payment.interval_length} {payment.interval_unit}
                  </div>
                  {payment.cw_agreement_name && (
                    <div className="text-xs text-primary-600 dark:text-primary-400 mt-1">
                      Linked to: {payment.cw_agreement_name}
                    </div>
                  )}
                </div>
                <div className="text-right flex flex-col items-end gap-2">
                  <span className={`inline-block px-2 py-1 text-xs rounded-full ${
                    payment.status?.toLowerCase() === 'active'
                      ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                      : 'bg-neutral-100 text-neutral-600 dark:bg-neutral-700 dark:text-neutral-400'
                  }`}>
                    {payment.status}
                  </span>
                  <div className="text-xs text-neutral-500">
                    Mode: {payment.application_mode || 'manual'}
                  </div>
                  <div className="flex items-center gap-2">
                    {payment.status?.toLowerCase() === 'active' && (
                      <button
                        onClick={() => setEditingPayment(payment)}
                        className="text-xs text-primary-600 hover:text-primary-700 flex items-center gap-1"
                      >
                        <Edit2 className="w-3 h-3" />
                        Edit
                      </button>
                    )}
                    {payment.status?.toLowerCase() === 'active' && (
                      <button
                        onClick={() => {
                          if (confirm('Are you sure you want to cancel this recurring payment?')) {
                            cancelMutation.mutate(payment.id);
                          }
                        }}
                        disabled={cancelMutation.isPending}
                        className="text-xs text-red-600 hover:text-red-700 flex items-center gap-1"
                      >
                        <X className="w-3 h-3" />
                        Cancel
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Create Recurring Payment Modal */}
      {showCreateModal && (
        <CreateRecurringPaymentModal
          companyId={companyId}
          paymentMethods={paymentMethods}
          onClose={() => setShowCreateModal(false)}
          onSuccess={() => {
            setShowCreateModal(false);
            refetch();
          }}
        />
      )}

      {/* Edit Recurring Payment Modal */}
      {editingPayment && (
        <EditRecurringPaymentModal
          companyId={companyId}
          payment={editingPayment}
          paymentMethods={paymentMethods}
          onClose={() => setEditingPayment(null)}
          onSuccess={() => {
            setEditingPayment(null);
            refetch();
          }}
        />
      )}
    </div>
  );
}

// ==========================================
// CREATE RECURRING PAYMENT MODAL
// ==========================================

function CreateRecurringPaymentModal({ companyId, paymentMethods, onClose, onSuccess }) {
  const [formData, setFormData] = useState({
    name: '',
    amount: '',
    paymentMethodId: paymentMethods[0]?.id || '',
    intervalLength: '1',
    intervalUnit: 'months',
    startDate: (() => {
      const tomorrow = new Date();
      tomorrow.setDate(tomorrow.getDate() + 1);
      return tomorrow.toLocaleDateString('en-CA'); // YYYY-MM-DD format in local timezone
    })(),
    totalOccurrences: '',
    applicationMode: 'manual',
    agreementId: '',
  });
  const [error, setError] = useState(null);

  // Fetch agreements for the company
  const { data: agreementsData, isLoading: agreementsLoading } = useQuery({
    queryKey: ['admin-company-agreements', companyId],
    queryFn: () => adminCompanyAPI.getAgreements(companyId),
    enabled: !!companyId,
  });

  const agreements = agreementsData?.data?.agreements || [];

  // Fetch agreement details when one is selected
  const { data: agreementDetailsData } = useQuery({
    queryKey: ['admin-agreement-details', companyId, formData.agreementId],
    queryFn: () => adminClientPaymentsAPI.getAgreementDetails(companyId, formData.agreementId),
    enabled: !!companyId && !!formData.agreementId,
  });

  // Auto-populate form when agreement details are loaded
  useEffect(() => {
    if (agreementDetailsData?.data?.agreement) {
      const details = agreementDetailsData.data.agreement;
      setFormData(prev => ({
        ...prev,
        name: prev.name || details.name || '',
        amount: details.recurringAmount > 0 ? details.recurringAmount.toString() : prev.amount,
        intervalLength: details.intervalLength?.toString() || prev.intervalLength,
        intervalUnit: details.intervalUnit || prev.intervalUnit,
      }));
    }
  }, [agreementDetailsData]);

  const mutation = useMutation({
    mutationFn: (data) => adminClientPaymentsAPI.createRecurringPayment(companyId, data),
    onSuccess: () => onSuccess(),
    onError: (err) => setError(err.response?.data?.error || 'Failed to create recurring payment'),
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    setError(null);

    // Find selected agreement name if one is selected
    const selectedAgreement = formData.agreementId
      ? agreements.find(a => a.id === parseInt(formData.agreementId))
      : null;

    mutation.mutate({
      name: formData.name,
      amount: parseFloat(formData.amount),
      paymentMethodId: formData.paymentMethodId,
      intervalLength: parseInt(formData.intervalLength),
      intervalUnit: formData.intervalUnit,
      startDate: formData.startDate,
      totalOccurrences: formData.totalOccurrences ? parseInt(formData.totalOccurrences) : null,
      applicationMode: formData.applicationMode,
      agreementId: formData.agreementId ? parseInt(formData.agreementId) : null,
      agreementName: selectedAgreement?.name || null,
    });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-white dark:bg-neutral-800 rounded-lg w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="px-6 py-4 border-b border-neutral-200 dark:border-neutral-700 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-neutral-900 dark:text-white">Create Recurring Payment</h2>
          <button onClick={onClose} className="text-neutral-400 hover:text-neutral-600">
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {error && (
            <div className="p-3 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-lg text-red-700 dark:text-red-300 text-sm">
              {error}
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">
              Name / Description
            </label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              placeholder="Monthly Service Fee"
              className="w-full px-3 py-2 border border-neutral-300 dark:border-neutral-600 bg-white dark:bg-neutral-700 rounded-lg"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">
              Amount
            </label>
            <input
              type="number"
              step="0.01"
              min="0.01"
              value={formData.amount}
              onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
              placeholder="0.00"
              className="w-full px-3 py-2 border border-neutral-300 dark:border-neutral-600 bg-white dark:bg-neutral-700 rounded-lg"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">
              Payment Method
            </label>
            <select
              value={formData.paymentMethodId}
              onChange={(e) => setFormData({ ...formData, paymentMethodId: e.target.value })}
              className="w-full px-3 py-2 border border-neutral-300 dark:border-neutral-600 bg-white dark:bg-neutral-700 rounded-lg"
              required
            >
              {paymentMethods.map((m) => (
                <option key={m.id} value={m.id}>
                  {m.payment_type === 'card'
                    ? `${m.card_type?.toUpperCase() || 'Card'} ****${m.last_four}`
                    : `${m.bank_name || 'Bank'} ****${m.account_last_four}`}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">
              Link to Agreement (optional)
            </label>
            <select
              value={formData.agreementId}
              onChange={(e) => setFormData({ ...formData, agreementId: e.target.value })}
              className="w-full px-3 py-2 border border-neutral-300 dark:border-neutral-600 bg-white dark:bg-neutral-700 rounded-lg"
              disabled={agreementsLoading}
            >
              <option value="">No agreement (apply to any invoice)</option>
              {agreements.map((agreement) => (
                <option key={agreement.id} value={agreement.id}>
                  {agreement.name}
                </option>
              ))}
            </select>
            <p className="mt-1 text-xs text-neutral-500">
              If linked, auto-apply payments will only apply to invoices for this agreement
            </p>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">
                Interval
              </label>
              <input
                type="number"
                min="1"
                max="365"
                value={formData.intervalLength}
                onChange={(e) => setFormData({ ...formData, intervalLength: e.target.value })}
                className="w-full px-3 py-2 border border-neutral-300 dark:border-neutral-600 bg-white dark:bg-neutral-700 rounded-lg"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">
                Unit
              </label>
              <select
                value={formData.intervalUnit}
                onChange={(e) => setFormData({ ...formData, intervalUnit: e.target.value })}
                className="w-full px-3 py-2 border border-neutral-300 dark:border-neutral-600 bg-white dark:bg-neutral-700 rounded-lg"
              >
                <option value="days">Days</option>
                <option value="months">Months</option>
              </select>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">
              Start Date
            </label>
            <input
              type="date"
              value={formData.startDate}
              onChange={(e) => setFormData({ ...formData, startDate: e.target.value })}
              min={new Date().toLocaleDateString('en-CA')}
              className="w-full px-3 py-2 border border-neutral-300 dark:border-neutral-600 bg-white dark:bg-neutral-700 rounded-lg"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">
              Total Occurrences (optional)
            </label>
            <input
              type="number"
              min="1"
              value={formData.totalOccurrences}
              onChange={(e) => setFormData({ ...formData, totalOccurrences: e.target.value })}
              placeholder="Leave blank for indefinite"
              className="w-full px-3 py-2 border border-neutral-300 dark:border-neutral-600 bg-white dark:bg-neutral-700 rounded-lg"
            />
          </div>

          <div className="border-t border-neutral-200 dark:border-neutral-700 pt-4">
            <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
              Application Mode
            </label>
            <div className="space-y-2">
              <label className="flex items-start gap-3 p-3 border border-neutral-200 dark:border-neutral-600 rounded-lg cursor-pointer hover:bg-neutral-50 dark:hover:bg-neutral-700/50">
                <input
                  type="radio"
                  name="applicationMode"
                  value="auto"
                  checked={formData.applicationMode === 'auto'}
                  onChange={(e) => setFormData({ ...formData, applicationMode: e.target.value })}
                  className="mt-1"
                />
                <div>
                  <div className="font-medium text-neutral-900 dark:text-white">Auto-apply to invoices</div>
                  <div className="text-sm text-neutral-500">
                    {formData.agreementId ? (
                      <>
                        Automatically apply payment to invoices <strong>for the selected agreement only</strong>.
                        Payments will apply to the oldest open invoice for this agreement first, then continue
                        to subsequent agreement invoices until fully applied.
                      </>
                    ) : (
                      <>
                        Automatically apply payment to <strong>any open invoice</strong> for this company.
                        Payments will apply to the oldest open invoice first, regardless of agreement,
                        then continue to subsequent invoices until fully applied.
                      </>
                    )}
                  </div>
                </div>
              </label>
              <label className="flex items-start gap-3 p-3 border border-neutral-200 dark:border-neutral-600 rounded-lg cursor-pointer hover:bg-neutral-50 dark:hover:bg-neutral-700/50">
                <input
                  type="radio"
                  name="applicationMode"
                  value="manual"
                  checked={formData.applicationMode === 'manual'}
                  onChange={(e) => setFormData({ ...formData, applicationMode: e.target.value })}
                  className="mt-1"
                />
                <div>
                  <div className="font-medium text-neutral-900 dark:text-white">Manual (add to credits)</div>
                  <div className="text-sm text-neutral-500">
                    {formData.agreementId ? (
                      <>
                        Add as unapplied credit on account. Admin can later apply this credit to any invoice
                        (not limited to the selected agreement).
                      </>
                    ) : (
                      <>
                        Add as unapplied credit on account. Admin can later choose which specific invoice(s)
                        to apply the credit to.
                      </>
                    )}
                  </div>
                </div>
              </label>
            </div>
          </div>

          <div className="flex justify-end gap-3 pt-4 border-t border-neutral-200 dark:border-neutral-700">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-neutral-600 dark:text-neutral-400 hover:text-neutral-800 dark:hover:text-neutral-200"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={mutation.isPending}
              className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 flex items-center gap-2"
            >
              {mutation.isPending && <Loader2 className="w-4 h-4 animate-spin" />}
              Create Recurring Payment
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ==========================================
// EDIT RECURRING PAYMENT MODAL
// ==========================================

function EditRecurringPaymentModal({ companyId, payment, paymentMethods, onClose, onSuccess }) {
  const [formData, setFormData] = useState({
    name: payment.name || '',
    amount: payment.amount?.toString() || '',
    paymentMethodId: payment.payment_method_id || paymentMethods[0]?.id || '',
    applicationMode: payment.application_mode || 'manual',
    agreementId: payment.cw_agreement_id?.toString() || '',
  });
  const [error, setError] = useState(null);
  const [showReassign, setShowReassign] = useState(false);
  const [companySearch, setCompanySearch] = useState('');
  const [newCompanyId, setNewCompanyId] = useState(null);
  const [newCompanyName, setNewCompanyName] = useState('');

  // Fetch agreements for the company
  const { data: agreementsData, isLoading: agreementsLoading } = useQuery({
    queryKey: ['admin-company-agreements', companyId],
    queryFn: () => adminCompanyAPI.getAgreements(companyId),
    enabled: !!companyId,
  });

  const agreements = agreementsData?.data?.agreements || [];

  // Company search for reassignment
  const { data: companySearchResults } = useQuery({
    queryKey: ['company-search', companySearch],
    // adminCompanyAPI.searchCompanies never existed; the move dialog's search returned nothing.
    queryFn: () => adminCompanyAPI.list({ search: companySearch, limit: 10 }),
    enabled: companySearch.length >= 2,
  });

  const mutation = useMutation({
    mutationFn: (data) => adminClientPaymentsAPI.updateRecurringPayment(companyId, payment.id, data),
    onSuccess: () => onSuccess(),
    onError: (err) => setError(err.response?.data?.error || 'Failed to update recurring payment'),
  });

  const reassignMutation = useMutation({
    mutationFn: (newCompanyId) => adminClientPaymentsAPI.reassignRecurringPayment(payment.id, newCompanyId),
    onSuccess: () => onSuccess(),
    onError: (err) => setError(err.response?.data?.error || 'Failed to reassign subscription'),
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    setError(null);

    // Find selected agreement name if one is selected
    const selectedAgreement = formData.agreementId
      ? agreements.find(a => a.id === parseInt(formData.agreementId))
      : null;

    mutation.mutate({
      name: formData.name,
      amount: parseFloat(formData.amount),
      paymentMethodId: formData.paymentMethodId,
      applicationMode: formData.applicationMode,
      agreementId: formData.agreementId ? parseInt(formData.agreementId) : null,
      agreementName: selectedAgreement?.name || null,
    });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-white dark:bg-neutral-800 rounded-lg w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="px-6 py-4 border-b border-neutral-200 dark:border-neutral-700 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-neutral-900 dark:text-white">Edit Recurring Payment</h2>
          <button onClick={onClose} className="text-neutral-400 hover:text-neutral-600">
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {error && (
            <div className="p-3 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-lg text-red-700 dark:text-red-300 text-sm">
              {error}
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">
              Name / Description
            </label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              placeholder="Monthly Service Fee"
              className="w-full px-3 py-2 border border-neutral-300 dark:border-neutral-600 bg-white dark:bg-neutral-700 rounded-lg text-neutral-900 dark:text-white"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">
              Amount
            </label>
            <input
              type="number"
              step="0.01"
              min="0.01"
              value={formData.amount}
              onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
              placeholder="0.00"
              className="w-full px-3 py-2 border border-neutral-300 dark:border-neutral-600 bg-white dark:bg-neutral-700 rounded-lg text-neutral-900 dark:text-white"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">
              Payment Method
            </label>
            <select
              value={formData.paymentMethodId}
              onChange={(e) => setFormData({ ...formData, paymentMethodId: e.target.value })}
              className="w-full px-3 py-2 border border-neutral-300 dark:border-neutral-600 bg-white dark:bg-neutral-700 rounded-lg text-neutral-900 dark:text-white"
              required
            >
              {paymentMethods.map((m) => (
                <option key={m.id} value={m.id}>
                  {m.payment_type === 'card'
                    ? `${m.card_type?.toUpperCase() || 'Card'} ****${m.last_four}`
                    : `${m.bank_name || 'Bank'} ****${m.account_last_four}`}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">
              Link to Agreement (optional)
            </label>
            <select
              value={formData.agreementId}
              onChange={(e) => setFormData({ ...formData, agreementId: e.target.value })}
              className="w-full px-3 py-2 border border-neutral-300 dark:border-neutral-600 bg-white dark:bg-neutral-700 rounded-lg text-neutral-900 dark:text-white"
              disabled={agreementsLoading}
            >
              <option value="">No agreement (apply to any invoice)</option>
              {agreements.map((agreement) => (
                <option key={agreement.id} value={agreement.id}>
                  {agreement.name}
                </option>
              ))}
            </select>
            <p className="mt-1 text-xs text-neutral-500">
              If linked, auto-apply payments will only apply to invoices for this agreement
            </p>
          </div>

          <div className="border-t border-neutral-200 dark:border-neutral-700 pt-4">
            <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
              Application Mode
            </label>
            <div className="space-y-2">
              <label className="flex items-start gap-3 p-3 border border-neutral-200 dark:border-neutral-600 rounded-lg cursor-pointer hover:bg-neutral-50 dark:hover:bg-neutral-700/50">
                <input
                  type="radio"
                  name="applicationMode"
                  value="auto"
                  checked={formData.applicationMode === 'auto'}
                  onChange={(e) => setFormData({ ...formData, applicationMode: e.target.value })}
                  className="mt-1"
                />
                <div>
                  <div className="font-medium text-neutral-900 dark:text-white">Auto-apply to invoices</div>
                  <div className="text-sm text-neutral-500">
                    Automatically apply payments to open invoices when charged
                  </div>
                </div>
              </label>
              <label className="flex items-start gap-3 p-3 border border-neutral-200 dark:border-neutral-600 rounded-lg cursor-pointer hover:bg-neutral-50 dark:hover:bg-neutral-700/50">
                <input
                  type="radio"
                  name="applicationMode"
                  value="manual"
                  checked={formData.applicationMode === 'manual'}
                  onChange={(e) => setFormData({ ...formData, applicationMode: e.target.value })}
                  className="mt-1"
                />
                <div>
                  <div className="font-medium text-neutral-900 dark:text-white">Manual (add to credits)</div>
                  <div className="text-sm text-neutral-500">
                    Add as unapplied credit. Admin can later apply to specific invoices.
                  </div>
                </div>
              </label>
            </div>
          </div>

          {/* Reassign Company Section */}
          <div className="border-t border-neutral-200 dark:border-neutral-700 pt-4">
            <button
              type="button"
              onClick={() => setShowReassign(!showReassign)}
              className="flex items-center gap-2 text-sm text-neutral-600 dark:text-neutral-400 hover:text-neutral-800 dark:hover:text-neutral-200"
            >
              <ArrowRightLeft className="w-4 h-4" />
              {showReassign ? 'Hide' : 'Reassign to Different Company'}
            </button>

            {showReassign && (
              <div className="mt-3 p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg">
                <p className="text-xs text-amber-700 dark:text-amber-300 mb-3">
                  This will move the subscription to a different company. The subscription will appear under the new company's recurring payments.
                </p>
                <div className="relative">
                  <input
                    type="text"
                    value={companySearch}
                    onChange={(e) => {
                      setCompanySearch(e.target.value);
                      setNewCompanyId(null);
                      setNewCompanyName('');
                    }}
                    placeholder="Search for company..."
                    className="w-full px-3 py-2 border border-neutral-300 dark:border-neutral-600 bg-white dark:bg-neutral-700 rounded-lg text-neutral-900 dark:text-white text-sm"
                  />
                  {companySearchResults?.data?.companies?.length > 0 && !newCompanyId && (
                    <div className="absolute z-10 w-full mt-1 bg-white dark:bg-neutral-800 border border-neutral-200 dark:border-neutral-700 rounded-lg shadow-lg max-h-40 overflow-auto">
                      {companySearchResults.data.companies
                        .filter(c => c.id !== companyId)
                        .map((company) => (
                          <button
                            key={company.id}
                            type="button"
                            onClick={() => {
                              setNewCompanyId(company.id);
                              setNewCompanyName(company.name);
                              setCompanySearch(company.name);
                            }}
                            className="w-full px-3 py-2 text-left text-sm hover:bg-neutral-100 dark:hover:bg-neutral-700"
                          >
                            {company.name} ({company.identifier})
                          </button>
                        ))}
                    </div>
                  )}
                </div>
                {newCompanyId && (
                  <div className="mt-3 flex items-center justify-between">
                    <span className="text-sm text-green-600 dark:text-green-400">
                      Move to: {newCompanyName}
                    </span>
                    <button
                      type="button"
                      onClick={() => {
                        if (confirm(`Are you sure you want to move this subscription to "${newCompanyName}"?`)) {
                          reassignMutation.mutate(newCompanyId);
                        }
                      }}
                      disabled={reassignMutation.isPending}
                      className="px-3 py-1.5 text-sm bg-amber-600 text-white rounded hover:bg-amber-700 disabled:opacity-50 flex items-center gap-2"
                    >
                      {reassignMutation.isPending && <Loader2 className="w-3 h-3 animate-spin" />}
                      Reassign
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="flex justify-end gap-3 pt-4 border-t border-neutral-200 dark:border-neutral-700">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-neutral-600 dark:text-neutral-400 hover:text-neutral-800 dark:hover:text-neutral-200"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={mutation.isPending}
              className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 flex items-center gap-2"
            >
              {mutation.isPending && <Loader2 className="w-4 h-4 animate-spin" />}
              Save Changes
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ==========================================
// AUTOPAY TAB
// ==========================================

function AutoPayTab({ companyId }) {
  const queryClient = useQueryClient();
  const [settings, setSettings] = useState({
    enabled: false,
    paymentMethodId: '',
    maxAmount: '',
    dayOfMonth: '',
  });

  const { data, isLoading } = useQuery({
    queryKey: ['admin-autopay', companyId],
    queryFn: () => adminClientPaymentsAPI.getAutoPay(companyId),
    enabled: !!companyId,
  });

  const { data: methodsData } = useQuery({
    queryKey: ['admin-payment-methods', companyId],
    queryFn: () => adminClientPaymentsAPI.getPaymentMethods(companyId),
    enabled: !!companyId,
  });

  const paymentMethods = methodsData?.data?.paymentMethods || [];

  useEffect(() => {
    if (data?.data?.autoPay) {
      const ap = data.data.autoPay;
      setSettings({
        enabled: true,
        paymentMethodId: ap.payment_method_id || '',
        maxAmount: ap.max_amount || '',
        dayOfMonth: ap.day_of_month || '',
      });
    }
  }, [data]);

  const mutation = useMutation({
    mutationFn: (data) => adminClientPaymentsAPI.configureAutoPay(companyId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-autopay', companyId] });
    },
  });

  const handleSave = () => {
    mutation.mutate({
      enabled: settings.enabled,
      paymentMethodId: settings.paymentMethodId || null,
      maxAmount: settings.maxAmount ? parseFloat(settings.maxAmount) : null,
      dayOfMonth: settings.dayOfMonth ? parseInt(settings.dayOfMonth) : null,
    });
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary-500" />
      </div>
    );
  }

  return (
    <div className="bg-white dark:bg-neutral-800 rounded-lg border border-neutral-200 dark:border-neutral-700 p-6">
      <h3 className="font-medium text-neutral-900 dark:text-white mb-6">AutoPay Configuration</h3>

      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <div className="font-medium text-neutral-900 dark:text-white">Enable AutoPay</div>
            <div className="text-sm text-neutral-500">Automatically pay invoices when due</div>
          </div>
          <button
            onClick={() => setSettings({ ...settings, enabled: !settings.enabled })}
            className={`relative inline-flex h-6 w-11 rounded-full transition-colors ${
              settings.enabled ? 'bg-primary-600' : 'bg-neutral-200 dark:bg-neutral-700'
            }`}
          >
            <span className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition ${
              settings.enabled ? 'translate-x-5' : 'translate-x-0.5'
            } mt-0.5`} />
          </button>
        </div>

        {settings.enabled && (
          <>
            <div>
              <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
                Payment Method
              </label>
              <select
                value={settings.paymentMethodId}
                onChange={(e) => setSettings({ ...settings, paymentMethodId: e.target.value })}
                className="w-full px-3 py-2 border border-neutral-300 dark:border-neutral-600 bg-white dark:bg-neutral-700 rounded-lg"
              >
                <option value="">Select a payment method</option>
                {paymentMethods.map((m) => (
                  <option key={m.id} value={m.id}>
                    {m.payment_type === 'card'
                      ? `${m.card_type?.toUpperCase()} ****${m.last_four}`
                      : `${m.bank_name} ****${m.account_last_four}`}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
                Maximum Amount (optional)
              </label>
              <input
                type="number"
                value={settings.maxAmount}
                onChange={(e) => setSettings({ ...settings, maxAmount: e.target.value })}
                placeholder="Leave blank for no limit"
                className="w-full px-3 py-2 border border-neutral-300 dark:border-neutral-600 bg-white dark:bg-neutral-700 rounded-lg"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
                Day of Month (optional)
              </label>
              <select
                value={settings.dayOfMonth}
                onChange={(e) => setSettings({ ...settings, dayOfMonth: e.target.value })}
                className="w-full px-3 py-2 border border-neutral-300 dark:border-neutral-600 bg-white dark:bg-neutral-700 rounded-lg"
              >
                <option value="">On each invoice's due date</option>
                {Array.from({ length: 31 }, (_, i) => i + 1).map((d) => (
                  <option key={d} value={d}>{d}</option>
                ))}
              </select>
              <p className="text-xs text-neutral-500 mt-1">In a shorter month, the last day of the month.</p>
            </div>
          </>
        )}

        <div className="pt-4 border-t border-neutral-200 dark:border-neutral-700">
          <button
            onClick={handleSave}
            disabled={mutation.isPending}
            className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 flex items-center gap-2"
          >
            {mutation.isPending && <Loader2 className="w-4 h-4 animate-spin" />}
            Save AutoPay Settings
          </button>
          {mutation.isSuccess && (
            <span className="ml-3 text-green-600 dark:text-green-400 text-sm">
              <CheckCircle className="w-4 h-4 inline mr-1" />
              Saved
            </span>
          )}
          {mutation.isError && (
            <span className="ml-3 text-red-600 text-sm">{mutation.error?.response?.data?.error || 'Could not save'}</span>
          )}
        </div>
      </div>

      <div className="mt-6">
        <AutopayPreviewPanel companyId={companyId} />
      </div>
    </div>
  );
}

// ==========================================
// DEPOSITS TAB
// ==========================================

function DepositsTab({ companyId }) {
  const queryClient = useQueryClient();
  const [statusFilter, setStatusFilter] = useState('all');
  const [linkModalDeposit, setLinkModalDeposit] = useState(null);

  const { data, isLoading, error } = useQuery({
    queryKey: ['admin-deposits', companyId, statusFilter],
    queryFn: () => depositsAPI.list({ companyId, status: statusFilter === 'all' ? undefined : statusFilter, limit: 100 }),
    enabled: !!companyId,
  });

  const deposits = data?.data?.deposits || [];
  const totals = data?.data?.totals;

  const formatCurrency = (n) => `$${(parseFloat(n) || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  const statusStyles = {
    pending: 'bg-amber-100 text-amber-800 dark:bg-amber-900/50 dark:text-amber-200',
    partial: 'bg-amber-100 text-amber-800 dark:bg-amber-900/50 dark:text-amber-200',
    paid: 'bg-green-100 text-green-800 dark:bg-green-900/50 dark:text-green-200',
    waived: 'bg-neutral-200 text-neutral-700 dark:bg-neutral-700 dark:text-neutral-300',
    refunded: 'bg-red-100 text-red-800 dark:bg-red-900/50 dark:text-red-200',
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-6 h-6 animate-spin text-primary-500" />
      </div>
    );
  }
  if (error) {
    return <div className="p-4 text-red-600">Failed to load deposits: {error.message}</div>;
  }

  return (
    <div className="space-y-4">
      {/* Header with status filter */}
      <div className="bg-white dark:bg-neutral-800 rounded-lg border border-neutral-200 dark:border-neutral-700 p-4">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h3 className="text-lg font-semibold text-neutral-900 dark:text-white">Quote Deposits</h3>
            <p className="text-sm text-neutral-500 dark:text-neutral-400">
              Deposit payments collected via accepted quotes. Link to a CW Down Payment invoice to apply them.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-sm text-neutral-600 dark:text-neutral-300">Status:</span>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="px-3 py-1.5 border border-neutral-300 dark:border-neutral-600 rounded-lg bg-white dark:bg-neutral-700 dark:text-white text-sm"
            >
              <option value="all">All</option>
              <option value="pending">Pending</option>
              <option value="partial">Partial</option>
              <option value="paid">Paid</option>
              <option value="waived">Waived</option>
              <option value="refunded">Refunded</option>
            </select>
          </div>
        </div>
        {totals && (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-4 pt-4 border-t border-neutral-200 dark:border-neutral-700">
            <div>
              <div className="text-xs text-neutral-500 uppercase tracking-wider">Pending</div>
              <div className="text-lg font-semibold text-neutral-900 dark:text-white">{totals.pendingCount}</div>
            </div>
            <div>
              <div className="text-xs text-neutral-500 uppercase tracking-wider">Pending $</div>
              <div className="text-lg font-semibold text-amber-700 dark:text-amber-300">{formatCurrency(totals.pendingAmount)}</div>
            </div>
            <div>
              <div className="text-xs text-neutral-500 uppercase tracking-wider">Partial</div>
              <div className="text-lg font-semibold text-neutral-900 dark:text-white">{totals.partialCount}</div>
            </div>
            <div>
              <div className="text-xs text-neutral-500 uppercase tracking-wider">Partial $</div>
              <div className="text-lg font-semibold text-amber-700 dark:text-amber-300">{formatCurrency(totals.partialAmount)}</div>
            </div>
          </div>
        )}
      </div>

      {/* Deposits list */}
      <div className="bg-white dark:bg-neutral-800 rounded-lg border border-neutral-200 dark:border-neutral-700 overflow-hidden">
        {deposits.length === 0 ? (
          <div className="p-12 text-center text-neutral-500 dark:text-neutral-400">
            <Banknote className="w-12 h-12 mx-auto mb-3 opacity-50" />
            No deposits for this company yet.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-neutral-50 dark:bg-neutral-700/50 text-left text-neutral-600 dark:text-neutral-300">
                <tr>
                  <th className="px-4 py-3 font-medium">Quote</th>
                  <th className="px-4 py-3 font-medium">Status</th>
                  <th className="px-4 py-3 font-medium text-right">Required</th>
                  <th className="px-4 py-3 font-medium text-right">Paid</th>
                  <th className="px-4 py-3 font-medium text-right">Remaining</th>
                  <th className="px-4 py-3 font-medium">Linked Invoice</th>
                  <th className="px-4 py-3 font-medium">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-neutral-200 dark:divide-neutral-700">
                {deposits.map((d) => (
                  <tr key={d.id} className="hover:bg-neutral-50 dark:hover:bg-neutral-700/30">
                    <td className="px-4 py-3">
                      <Link to={`/quotes/${d.opportunityId}?from=admin`} className="text-primary-600 dark:text-primary-400 hover:underline font-medium">
                        Quote #{d.opportunityId}
                      </Link>
                      {d.signerName && (
                        <div className="text-xs text-neutral-500 dark:text-neutral-400">{d.signerName}</div>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium capitalize ${statusStyles[d.status] || 'bg-neutral-100 text-neutral-700'}`}>
                        {d.status}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right text-neutral-900 dark:text-white">{formatCurrency(d.depositAmount)}</td>
                    <td className={`px-4 py-3 text-right ${d.amountPaid > 0 ? 'text-green-600 dark:text-green-400' : 'text-neutral-400'}`}>
                      {formatCurrency(d.amountPaid)}
                    </td>
                    <td className="px-4 py-3 text-right text-amber-700 dark:text-amber-300">
                      {formatCurrency(d.amountRemaining)}
                    </td>
                    <td className="px-4 py-3">
                      {d.cwInvoiceId ? (
                        <Link to={`/admin/invoices/${d.cwInvoiceId}`} className="inline-flex items-center gap-1 text-primary-600 dark:text-primary-400 hover:underline">
                          <Link2 className="w-3.5 h-3.5" />
                          #{d.cwInvoiceId}
                        </Link>
                      ) : (
                        <span className="text-neutral-400 text-xs">Not linked</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      {!d.cwInvoiceId && d.amountPaid > 0 && (
                        <button
                          onClick={() => setLinkModalDeposit(d)}
                          className="text-xs px-2 py-1 bg-primary-600 hover:bg-primary-700 text-white rounded"
                        >
                          Link to Invoice
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Link to invoice modal */}
      {linkModalDeposit && (
        <LinkDepositToInvoiceModal
          deposit={linkModalDeposit}
          onClose={() => setLinkModalDeposit(null)}
          onSuccess={() => {
            setLinkModalDeposit(null);
            queryClient.invalidateQueries({ queryKey: ['admin-deposits', companyId] });
          }}
        />
      )}
    </div>
  );
}

function LinkDepositToInvoiceModal({ deposit, onClose, onSuccess }) {
  const [invoiceIdInput, setInvoiceIdInput] = useState('');
  const [invoiceInfo, setInvoiceInfo] = useState(null);
  const [step, setStep] = useState('enter'); // 'enter' | 'confirm' | 'processing'
  const [error, setError] = useState(null);
  const [warning, setWarning] = useState(null);
  const [result, setResult] = useState(null);

  const lookupMutation = useMutation({
    mutationFn: (id) => depositsAPI.getInvoiceInfo(id),
    onSuccess: (res) => {
      const info = res.data;
      setInvoiceInfo(info);
      setStep('confirm');
      setWarning(info.isDownPayment ? null : `This is a "${info.type || 'regular'}" invoice, not a Down Payment invoice. Applying the deposit as a payment here may double-count if a separate Down Payment invoice exists.`);
    },
    onError: (err) => setError(err.response?.data?.error || 'Could not find invoice'),
  });

  const linkMutation = useMutation({
    mutationFn: (payload) => depositsAPI.linkInvoice(deposit.id, payload),
    onSuccess: (res) => {
      setResult(res.data);
      if (res.data.cwPaymentError) {
        setError(`Linked, but failed to post payment: ${res.data.cwPaymentError}`);
      } else {
        setTimeout(onSuccess, 1500);
      }
    },
    onError: (err) => setError(err.response?.data?.error || 'Failed to link'),
  });

  const handleLookup = () => {
    setError(null);
    const id = parseInt(invoiceIdInput, 10);
    if (!id) { setError('Enter a valid invoice ID'); return; }
    lookupMutation.mutate(id);
  };

  const handleConfirm = (applyAsPayment) => {
    setError(null);
    linkMutation.mutate({ invoiceId: invoiceInfo.id, applyAsPayment });
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-neutral-800 rounded-xl shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto">
        <div className="px-6 py-4 border-b border-neutral-200 dark:border-neutral-700 flex items-center justify-between">
          <h3 className="text-lg font-semibold text-neutral-900 dark:text-white">Link Deposit to CW Invoice</h3>
          <button onClick={onClose}><X className="w-5 h-5 text-neutral-500" /></button>
        </div>

        <div className="p-6 space-y-4">
          <div className="text-sm text-neutral-600 dark:text-neutral-300">
            Quote #{deposit.opportunityId} — deposit paid: <span className="font-semibold">${deposit.amountPaid.toFixed(2)}</span>
          </div>

          {error && (
            <div className="p-3 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-lg text-sm text-red-700 dark:text-red-300">
              {error}
            </div>
          )}

          {result?.success && !error && (
            <div className="p-3 bg-green-50 dark:bg-green-900/30 border border-green-200 dark:border-green-800 rounded-lg text-sm text-green-700 dark:text-green-300">
              Linked successfully.
              {result.cwPayment && <div className="mt-1">CW payment of ${result.cwPayment.amount.toFixed(2)} posted on invoice #{result.cwPayment.invoiceId}.</div>}
            </div>
          )}

          {step === 'enter' && !result?.success && (
            <>
              <div>
                <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">CW Invoice ID</label>
                <input
                  type="number"
                  value={invoiceIdInput}
                  onChange={(e) => setInvoiceIdInput(e.target.value)}
                  placeholder="e.g., 12345"
                  className="w-full px-3 py-2 border border-neutral-300 dark:border-neutral-600 rounded-lg bg-white dark:bg-neutral-700 dark:text-white"
                />
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <button onClick={onClose} className="px-4 py-2 text-sm text-neutral-600 dark:text-neutral-300">Cancel</button>
                <button
                  onClick={handleLookup}
                  disabled={lookupMutation.isPending || !invoiceIdInput}
                  className="px-4 py-2 text-sm bg-primary-600 hover:bg-primary-700 text-white rounded-lg disabled:opacity-50"
                >
                  {lookupMutation.isPending ? 'Looking up...' : 'Next'}
                </button>
              </div>
            </>
          )}

          {step === 'confirm' && invoiceInfo && !result?.success && (
            <>
              <div className="bg-neutral-50 dark:bg-neutral-700/50 rounded-lg p-3 space-y-1 text-sm">
                <div className="flex justify-between"><span className="text-neutral-500">Invoice #</span><span className="font-medium dark:text-white">{invoiceInfo.invoiceNumber}</span></div>
                <div className="flex justify-between"><span className="text-neutral-500">Type</span><span className={`font-medium ${invoiceInfo.isDownPayment ? 'text-green-600 dark:text-green-400' : 'dark:text-white'}`}>{invoiceInfo.type || 'Standard'}</span></div>
                <div className="flex justify-between"><span className="text-neutral-500">Company</span><span className="font-medium dark:text-white">{invoiceInfo.companyName || 'N/A'}</span></div>
                <div className="flex justify-between"><span className="text-neutral-500">Total</span><span className="font-medium dark:text-white">${invoiceInfo.total.toFixed(2)}</span></div>
                <div className="flex justify-between"><span className="text-neutral-500">Balance</span><span className="font-medium dark:text-white">${invoiceInfo.balance.toFixed(2)}</span></div>
              </div>

              {warning && (
                <div className="p-3 bg-amber-50 dark:bg-amber-900/30 border border-amber-200 dark:border-amber-800 rounded-lg text-sm text-amber-800 dark:text-amber-200">
                  ⚠️ {warning}
                </div>
              )}

              <p className="text-sm text-neutral-600 dark:text-neutral-300">
                {invoiceInfo.isDownPayment
                  ? `This will link the deposit AND post ${formatMoney(deposit.amountPaid)} as a payment on this Down Payment invoice. Continue?`
                  : `Link only (no CW payment posted) — recommended for non-Down-Payment invoices.`}
              </p>

              <div className="flex justify-end gap-2 pt-2">
                <button onClick={() => setStep('enter')} className="px-4 py-2 text-sm text-neutral-600 dark:text-neutral-300">Back</button>
                <button
                  onClick={() => handleConfirm(false)}
                  disabled={linkMutation.isPending}
                  className="px-4 py-2 text-sm border border-neutral-300 dark:border-neutral-600 rounded-lg dark:text-white"
                >
                  Link only
                </button>
                <button
                  onClick={() => handleConfirm(true)}
                  disabled={linkMutation.isPending}
                  className={`px-4 py-2 text-sm rounded-lg text-white ${invoiceInfo.isDownPayment ? 'bg-primary-600 hover:bg-primary-700' : 'bg-amber-600 hover:bg-amber-700'} disabled:opacity-50`}
                >
                  {linkMutation.isPending ? 'Processing...' : (invoiceInfo.isDownPayment ? 'Link & apply payment' : 'Link & apply anyway')}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// ==========================================
// CREDITS TAB
// ==========================================

function CreditsTab({ companyId }) {
  const queryClient = useQueryClient();
  const [showAddCreditModal, setShowAddCreditModal] = useState(false);
  const [applyingCredit, setApplyingCredit] = useState(null);
  const [appliedMessage, setAppliedMessage] = useState(null);

  const { data, isLoading, refetch } = useQuery({
    queryKey: ['admin-credits', companyId],
    queryFn: () => adminClientPaymentsAPI.getCredits(companyId),
    enabled: !!companyId,
  });

  const credits = data?.data?.credits || [];
  const totalAvailable = data?.data?.totalAvailable || 0;
  const recentApplications = data?.data?.recentApplications || [];

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Summary */}
      <div className="bg-primary-50 dark:bg-primary-900/20 rounded-lg p-4 flex items-center justify-between">
        <div>
          <div className="text-sm text-primary-600 dark:text-primary-400">Total Available Credit</div>
          <div className="text-2xl font-bold text-primary-700 dark:text-primary-300">
            ${totalAvailable.toFixed(2)}
          </div>
        </div>
        <button
          onClick={() => setShowAddCreditModal(true)}
          className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
        >
          <Plus className="w-4 h-4" />
          Add Credit
        </button>
      </div>

      {appliedMessage && (
        <div className="p-3 bg-green-50 dark:bg-green-900/30 border border-green-200 dark:border-green-800 rounded-lg text-green-700 dark:text-green-300 text-sm">
          {appliedMessage}
        </div>
      )}

      {/* Credits List */}
      <div className="bg-white dark:bg-neutral-800 rounded-lg border border-neutral-200 dark:border-neutral-700">
        <div className="px-4 py-3 border-b border-neutral-200 dark:border-neutral-700">
          <h3 className="font-medium text-neutral-900 dark:text-white">Account Credits</h3>
        </div>

        {credits.length === 0 ? (
          <div className="p-8 text-center text-neutral-500">
            <DollarSign className="w-12 h-12 text-neutral-300 mx-auto mb-3" />
            <p>No credits on account</p>
          </div>
        ) : (
          <div className="divide-y divide-neutral-200 dark:divide-neutral-700">
            {credits.map((credit) => (
              <div key={credit.id} className="p-4 flex items-center justify-between">
                <div>
                  <div className="font-medium text-neutral-900 dark:text-white">
                    {credit.description || 'Credit'}
                  </div>
                  <div className="text-sm text-neutral-500 mt-1">
                    {new Date(credit.payment_date).toLocaleDateString()} - {credit.source}
                  </div>
                </div>
                <div className="text-right">
                  <div className={`font-medium ${
                    credit.is_fully_applied
                      ? 'text-neutral-400'
                      : 'text-green-600 dark:text-green-400'
                  }`}>
                    ${parseFloat(credit.remaining_amount).toFixed(2)} remaining
                  </div>
                  <div className="text-xs text-neutral-500">
                    of ${parseFloat(credit.amount).toFixed(2)}
                  </div>
                  {!credit.is_fully_applied && (
                    <button
                      onClick={() => { setAppliedMessage(null); setApplyingCredit(credit); }}
                      className="mt-2 text-sm text-primary-600 hover:text-primary-700 flex items-center gap-1"
                    >
                      Apply to Invoice <ArrowRight className="w-3 h-3" />
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Recent Applications */}
      {recentApplications.length > 0 && (
        <div className="bg-white dark:bg-neutral-800 rounded-lg border border-neutral-200 dark:border-neutral-700">
          <div className="px-4 py-3 border-b border-neutral-200 dark:border-neutral-700">
            <h3 className="font-medium text-neutral-900 dark:text-white">Recent Applications</h3>
          </div>
          <div className="divide-y divide-neutral-200 dark:divide-neutral-700">
            {recentApplications.map((app) => (
              <div key={app.id} className="px-4 py-3 flex items-center justify-between text-sm">
                <div>
                  <span className="text-neutral-900 dark:text-white">
                    ${parseFloat(app.amount).toFixed(2)}
                  </span>
                  <span className="text-neutral-500 mx-2">applied to</span>
                  <span className="text-primary-600">Invoice #{app.invoice_id}</span>
                </div>
                <div className="text-neutral-500">
                  {new Date(app.applied_at).toLocaleString()}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Apply Credit Modal */}
      {applyingCredit && (
        <ApplyCreditModal
          companyId={companyId}
          credit={applyingCredit}
          onClose={() => setApplyingCredit(null)}
          onSuccess={(result) => {
            const app = result?.application;
            setApplyingCredit(null);
            setAppliedMessage(
              app
                ? `Applied ${formatMoney(parseFloat(app.amount))} to invoice #${app.invoice_id}.`
                : 'Credit applied.'
            );
            refetch();
            queryClient.invalidateQueries({ queryKey: ['admin-credit-open-invoices', companyId] });
          }}
        />
      )}

      {/* Add Credit Modal */}
      {showAddCreditModal && (
        <AddCreditModal
          companyId={companyId}
          onClose={() => setShowAddCreditModal(false)}
          onSuccess={() => {
            setShowAddCreditModal(false);
            refetch();
          }}
        />
      )}
    </div>
  );
}

// ==========================================
// ADD CREDIT MODAL
// ==========================================

function AddCreditModal({ companyId, onClose, onSuccess }) {
  const [formData, setFormData] = useState({
    amount: '',
    description: '',
    // Local-TZ "today" so the form default matches the user's perception.
    paymentDate: new Date().toLocaleDateString('en-CA'),
  });
  const [error, setError] = useState(null);

  const mutation = useMutation({
    mutationFn: (data) => adminClientPaymentsAPI.createCredit(companyId, data),
    onSuccess: () => onSuccess(),
    onError: (err) => setError(err.response?.data?.error || 'Failed to create credit'),
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    setError(null);
    mutation.mutate({
      ...formData,
      amount: parseFloat(formData.amount),
    });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-white dark:bg-neutral-800 rounded-lg w-full max-w-md max-h-[90vh] overflow-y-auto">
        <div className="px-6 py-4 border-b border-neutral-200 dark:border-neutral-700 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-neutral-900 dark:text-white">Add Credit</h2>
          <button onClick={onClose} className="text-neutral-400 hover:text-neutral-600">
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {error && (
            <div className="p-3 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-lg text-red-700 dark:text-red-300 text-sm">
              {error}
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">
              Amount
            </label>
            <input
              type="number"
              step="0.01"
              min="0.01"
              value={formData.amount}
              onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
              placeholder="0.00"
              className="w-full px-3 py-2 border border-neutral-300 dark:border-neutral-600 bg-white dark:bg-neutral-700 rounded-lg"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">
              Description
            </label>
            <input
              type="text"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder="Manual prepayment"
              className="w-full px-3 py-2 border border-neutral-300 dark:border-neutral-600 bg-white dark:bg-neutral-700 rounded-lg"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">
              Date
            </label>
            <input
              type="date"
              value={formData.paymentDate}
              onChange={(e) => setFormData({ ...formData, paymentDate: e.target.value })}
              className="w-full px-3 py-2 border border-neutral-300 dark:border-neutral-600 bg-white dark:bg-neutral-700 rounded-lg"
            />
          </div>

          <div className="flex justify-end gap-3 pt-4 border-t border-neutral-200 dark:border-neutral-700">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-neutral-600 dark:text-neutral-400"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={mutation.isPending}
              className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 flex items-center gap-2"
            >
              {mutation.isPending && <Loader2 className="w-4 h-4 animate-spin" />}
              Add Credit
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ==========================================
// HISTORY TAB
// ==========================================

function HistoryTab({ companyId }) {
  const [typeFilter, setTypeFilter] = useState('all');
  const [refunding, setRefunding] = useState(null);
  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ['admin-unified-history', companyId],
    queryFn: () => adminClientPaymentsAPI.getUnifiedHistory(companyId),
    enabled: !!companyId,
  });

  const events = data?.data?.events || [];
  const filteredEvents = typeFilter === 'all'
    ? events
    : events.filter(e => e.type === typeFilter);

  const formatCurrency = (n) => {
    const abs = Math.abs(parseFloat(n) || 0);
    const formatted = `$${abs.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    return parseFloat(n) < 0 ? `-${formatted}` : formatted;
  };

  const typeStyles = {
    payment: 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-200',
    deposit: 'bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-200',
    refund: 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-200',
    void: 'bg-neutral-200 text-neutral-700 dark:bg-neutral-700 dark:text-neutral-300',
    credit: 'bg-purple-100 text-purple-800 dark:bg-purple-900/40 dark:text-purple-200',
  };

  const counts = events.reduce((acc, e) => {
    acc[e.type] = (acc[e.type] || 0) + 1;
    acc.all = (acc.all || 0) + 1;
    return acc;
  }, {});

  const FILTERS = [
    { key: 'all', label: 'All' },
    { key: 'payment', label: 'Payments' },
    { key: 'deposit', label: 'Deposits' },
    { key: 'refund', label: 'Refunds' },
    { key: 'void', label: 'Voids' },
    { key: 'credit', label: 'Credits' },
  ];

  const renderMethod = (m) => {
    if (!m) return 'N/A';
    if (m.kind === 'credit') return 'Credit';
    if (m.kind === 'ach') {
      const last = m.last4 ? ` ****${m.last4}` : '';
      return (
        <div className="leading-tight">
          <div>ACH{last}</div>
          {m.bankName && <div className="text-xs text-neutral-500 dark:text-neutral-400">{m.bankName}</div>}
        </div>
      );
    }
    return `${m.label || 'Card'}${m.last4 ? ` ****${m.last4}` : ''}`;
  };

  const renderReference = (ref) => {
    if (!ref) return <span className="text-neutral-400 text-xs">—</span>;
    const parts = [];
    if (ref.invoices && ref.invoices.length > 0) {
      parts.push(
        ref.invoices.map(invId => (
          <Link key={invId} to={`/admin/invoices/${invId}`} className="text-primary-600 dark:text-primary-400 hover:underline mr-2">#{invId}</Link>
        ))
      );
    }
    if (ref.opportunityId) {
      parts.push(<Link key={`q-${ref.opportunityId}`} to={`/quotes/${ref.opportunityId}?from=admin`} className="text-primary-600 dark:text-primary-400 hover:underline">Quote #{ref.opportunityId}</Link>);
    }
    if (ref.transactionId) {
      parts.push(<div key="txn" className="text-xs text-neutral-500 dark:text-neutral-400 font-mono mt-0.5">{ref.transactionId}</div>);
    }
    if (ref.creditSource) {
      parts.push(<div key="src" className="text-xs text-neutral-500 dark:text-neutral-400 capitalize">{ref.creditSource}{ref.remaining > 0 ? ` · ${formatMoney(ref.remaining)} remaining` : ''}</div>);
    }
    return parts.length > 0 ? <div>{parts}</div> : <span className="text-neutral-400 text-xs">—</span>;
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary-500" />
      </div>
    );
  }

  return (
    <div className="bg-white dark:bg-neutral-800 rounded-lg border border-neutral-200 dark:border-neutral-700">
      <div className="px-4 py-3 border-b border-neutral-200 dark:border-neutral-700 flex items-center justify-between flex-wrap gap-3">
        <h3 className="font-medium text-neutral-900 dark:text-white">Payment History</h3>
        <div className="flex flex-wrap gap-1.5">
          {FILTERS.map(f => (
            <button
              key={f.key}
              onClick={() => setTypeFilter(f.key)}
              className={`px-3 py-1 text-xs rounded-full transition-colors ${
                typeFilter === f.key
                  ? 'bg-primary-600 text-white'
                  : 'bg-neutral-100 dark:bg-neutral-700 text-neutral-600 dark:text-neutral-300 hover:bg-neutral-200 dark:hover:bg-neutral-600'
              }`}
            >
              {f.label} {counts[f.key] !== undefined ? `(${counts[f.key]})` : ''}
            </button>
          ))}
        </div>
      </div>

      {filteredEvents.length === 0 ? (
        <div className="p-8 text-center text-neutral-500">
          <History className="w-12 h-12 text-neutral-300 mx-auto mb-3" />
          <p>{typeFilter === 'all' ? 'No payment activity yet' : `No ${typeFilter} events`}</p>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-neutral-50 dark:bg-neutral-900/50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-neutral-500 uppercase">Date</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-neutral-500 uppercase">Type</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-neutral-500 uppercase">Amount</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-neutral-500 uppercase">Method</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-neutral-500 uppercase">Reference</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-neutral-500 uppercase">Status</th>
                <th className="px-4 py-3"><span className="sr-only">Actions</span></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-neutral-200 dark:divide-neutral-700">
              {filteredEvents.map((e) => (
                <tr key={e.id} className="hover:bg-neutral-50 dark:hover:bg-neutral-700/30">
                  <td className="px-4 py-3 text-sm text-neutral-900 dark:text-white whitespace-nowrap">
                    {new Date(e.date).toLocaleDateString()}
                    <div className="text-xs text-neutral-500">{new Date(e.date).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</div>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium capitalize ${typeStyles[e.type] || 'bg-neutral-100 text-neutral-700'}`}>
                      {e.type}
                    </span>
                  </td>
                  <td className={`px-4 py-3 text-right font-medium text-sm whitespace-nowrap ${e.amount < 0 ? 'text-red-600 dark:text-red-400' : 'text-neutral-900 dark:text-white'}`}>
                    {formatCurrency(e.amount)}
                  </td>
                  <td className="px-4 py-3 text-sm text-neutral-600 dark:text-neutral-300">
                    {renderMethod(e.method)}
                  </td>
                  <td className="px-4 py-3 text-sm">
                    {renderReference(e.reference, e.type)}
                    {e.notes && <div className="text-xs text-neutral-500 dark:text-neutral-400 mt-0.5 italic">{e.notes}</div>}
                  </td>
                  <td className="px-4 py-3">
                    <span className={`inline-block px-2 py-1 text-xs rounded-full ${e.status === 'requires_action' ? '' : 'capitalize'} ${
                      e.status === 'completed' || e.status === 'success' || e.status === 'paid'
                        ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                        : e.status === 'partial' || e.status === 'pending' || e.status === 'requires_action'
                          ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400'
                          : 'bg-neutral-100 text-neutral-600 dark:bg-neutral-700 dark:text-neutral-400'
                    }`}>
                      {e.status === 'requires_action' ? 'Waiting for customer authentication' : e.status || 'completed'}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right">
                    {e.refund && e.status === 'completed' && e.refund.refundable > 0 && (
                      <button
                        onClick={() => setRefunding(e.refund)}
                        className="text-sm text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300"
                      >
                        Refund
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {refunding && (
        <RefundPaymentModal
          source={refunding.source}
          paymentId={refunding.paymentId}
          onDone={() => queryClient.invalidateQueries({ queryKey: ['admin-unified-history', companyId] })}
          onClose={() => setRefunding(null)}
        />
      )}
    </div>
  );
}

// ==========================================
// IMPORT ARB SUBSCRIPTIONS MODAL
// ==========================================

function ImportARBModal({ onClose, onImportComplete }) {
  const [status, setStatus] = useState('active');
  const [selectedSubscriptions, setSelectedSubscriptions] = useState({});
  const [companyAssignments, setCompanyAssignments] = useState({});
  const [companySearch, setCompanySearch] = useState({});
  const [showCompanyDropdown, setShowCompanyDropdown] = useState({});
  const [importProgress, setImportProgress] = useState(null);

  // Fetch ARB subscriptions
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['arb-subscriptions', status],
    queryFn: () => adminClientPaymentsAPI.getARBSubscriptions(status),
  });

  const subscriptions = data?.data?.subscriptions || [];
  const summary = data?.data?.summary || {};

  // Search companies for assignment
  const searchCompanies = async (query) => {
    if (query.length < 2) return [];
    try {
      const res = await adminCompanyAPI.list({ search: query, limit: 10 });
      return res.data?.companies || [];
    } catch {
      return [];
    }
  };

  // Import mutation
  const importMutation = useMutation({
    mutationFn: (subscriptions) => adminClientPaymentsAPI.importARBSubscriptions(subscriptions),
    onSuccess: (result) => {
      setImportProgress({
        complete: true,
        imported: result.data.imported,
        failed: result.data.failed,
        results: result.data.results,
        errors: result.data.errors,
      });
    },
    onError: (err) => {
      setImportProgress({
        complete: true,
        error: err.response?.data?.error || 'Import failed',
      });
    },
  });

  const handleSelectSubscription = (subId, checked) => {
    setSelectedSubscriptions(prev => ({
      ...prev,
      [subId]: checked
    }));
  };

  const handleSelectAll = (checked) => {
    const importable = subscriptions.filter(s => !s.isImported);
    const newSelected = {};
    importable.forEach(s => {
      newSelected[s.subscriptionId] = checked;
    });
    setSelectedSubscriptions(newSelected);
  };

  const handleAssignCompany = (subId, company) => {
    setCompanyAssignments(prev => ({
      ...prev,
      [subId]: company
    }));
    setShowCompanyDropdown(prev => ({ ...prev, [subId]: false }));
    setCompanySearch(prev => ({ ...prev, [subId]: company.name }));
  };

  const handleImport = () => {
    const toImport = Object.entries(selectedSubscriptions)
      .filter(([_, selected]) => selected)
      .map(([subId]) => {
        const sub = subscriptions.find(s => s.subscriptionId === subId);
        const assignedCompany = companyAssignments[subId];
        return {
          subscriptionId: subId,
          companyId: assignedCompany?.cw_company_id || sub?.matchedCompanyId
        };
      })
      .filter(s => s.companyId); // Only import if we have a company ID

    if (toImport.length === 0) {
      alert('Please select subscriptions and assign companies to import');
      return;
    }

    setImportProgress({ inProgress: true, total: toImport.length });
    importMutation.mutate(toImport);
  };

  const selectedCount = Object.values(selectedSubscriptions).filter(Boolean).length;
  const readyToImport = Object.entries(selectedSubscriptions)
    .filter(([_, selected]) => selected)
    .filter(([subId]) => {
      const sub = subscriptions.find(s => s.subscriptionId === subId);
      return companyAssignments[subId]?.cw_company_id || sub?.matchedCompanyId;
    }).length;

  // Company search state per subscription
  const [searchResults, setSearchResults] = useState({});

  const handleCompanySearch = async (subId, query) => {
    setCompanySearch(prev => ({ ...prev, [subId]: query }));
    if (query.length >= 2) {
      const results = await searchCompanies(query);
      setSearchResults(prev => ({ ...prev, [subId]: results }));
      setShowCompanyDropdown(prev => ({ ...prev, [subId]: true }));
    } else {
      setSearchResults(prev => ({ ...prev, [subId]: [] }));
      setShowCompanyDropdown(prev => ({ ...prev, [subId]: false }));
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-white dark:bg-neutral-800 rounded-lg shadow-xl w-full max-w-5xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-neutral-200 dark:border-neutral-700">
          <div>
            <h2 className="text-xl font-semibold text-neutral-900 dark:text-white">
              Import ARB Subscriptions
            </h2>
            <p className="text-sm text-neutral-500 dark:text-neutral-400 mt-1">
              Import existing Authorize.net subscriptions and link them to companies
            </p>
          </div>
          <button onClick={onClose} className="text-neutral-400 hover:text-neutral-600 dark:hover:text-neutral-200">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Status Filter */}
        <div className="px-6 py-3 border-b border-neutral-200 dark:border-neutral-700 flex items-center gap-4">
          <span className="text-sm text-neutral-600 dark:text-neutral-400">Status:</span>
          <div className="flex gap-2">
            {['active', 'inactive', 'expiring'].map((s) => (
              <button
                key={s}
                onClick={() => setStatus(s)}
                className={`px-3 py-1 text-sm rounded-full transition-colors ${
                  status === s
                    ? 'bg-primary-100 text-primary-700 dark:bg-primary-900/50 dark:text-primary-300'
                    : 'bg-neutral-100 text-neutral-600 dark:bg-neutral-700 dark:text-neutral-300 hover:bg-neutral-200 dark:hover:bg-neutral-600'
                }`}
              >
                {s.charAt(0).toUpperCase() + s.slice(1)}
              </button>
            ))}
          </div>
          {summary.total > 0 && (
            <div className="ml-auto flex gap-4 text-sm">
              <span className="text-neutral-500">Total: {summary.total}</span>
              <span className="text-green-600 dark:text-green-400">Available: {summary.available || 0}</span>
              <span className="text-amber-600 dark:text-amber-400">Unmatched: {summary.unmatched}</span>
              <span className="text-purple-500">Portal Created: {summary.portalCreated || 0}</span>
              <span className="text-neutral-400">Imported: {summary.previouslyImported || summary.imported || 0}</span>
            </div>
          )}
        </div>

        {/* Content */}
        <div className="flex-1 overflow-auto p-6">
          {isLoading ? (
            <div className="flex items-center justify-center h-64">
              <Loader2 className="w-8 h-8 animate-spin text-primary-500" />
            </div>
          ) : error ? (
            <div className="bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-lg p-4 flex items-center gap-2">
              <AlertCircle className="w-5 h-5 text-red-500" />
              <span className="text-red-700 dark:text-red-300">Failed to load subscriptions</span>
            </div>
          ) : importProgress?.complete ? (
            <div className="space-y-4">
              {importProgress.error ? (
                <div className="bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-lg p-4">
                  <AlertCircle className="w-5 h-5 text-red-500 inline mr-2" />
                  <span className="text-red-700 dark:text-red-300">{importProgress.error}</span>
                </div>
              ) : (
                <>
                  <div className="bg-green-50 dark:bg-green-900/30 border border-green-200 dark:border-green-800 rounded-lg p-4">
                    <CheckCircle className="w-5 h-5 text-green-500 inline mr-2" />
                    <span className="text-green-700 dark:text-green-300">
                      Successfully imported {importProgress.imported} subscription{importProgress.imported !== 1 ? 's' : ''}
                    </span>
                  </div>
                  {importProgress.failed > 0 && (
                    <div className="bg-amber-50 dark:bg-amber-900/30 border border-amber-200 dark:border-amber-800 rounded-lg p-4">
                      <AlertCircle className="w-5 h-5 text-amber-500 inline mr-2" />
                      <span className="text-amber-700 dark:text-amber-300">
                        {importProgress.failed} subscription{importProgress.failed !== 1 ? 's' : ''} failed to import
                      </span>
                      <ul className="mt-2 text-sm text-amber-600 dark:text-amber-400">
                        {importProgress.errors?.map((err, i) => (
                          <li key={i}>• {err.subscriptionId}: {err.error}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </>
              )}
              <div className="flex justify-end gap-3">
                <button
                  onClick={() => {
                    setImportProgress(null);
                    setSelectedSubscriptions({});
                    refetch();
                  }}
                  className="px-4 py-2 text-neutral-700 dark:text-neutral-300 hover:bg-neutral-100 dark:hover:bg-neutral-700 rounded-lg"
                >
                  Import More
                </button>
                <button
                  onClick={() => {
                    onImportComplete();
                  }}
                  className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
                >
                  Done
                </button>
              </div>
            </div>
          ) : subscriptions.length === 0 ? (
            <div className="text-center py-12">
              <RefreshCcw className="w-12 h-12 text-neutral-300 dark:text-neutral-600 mx-auto mb-4" />
              <p className="text-neutral-500 dark:text-neutral-400">No {status} subscriptions found</p>
            </div>
          ) : (
            <div className="space-y-2">
              {/* Select All */}
              <div className="flex items-center gap-2 mb-4 pb-2 border-b border-neutral-200 dark:border-neutral-700">
                <input
                  type="checkbox"
                  onChange={(e) => handleSelectAll(e.target.checked)}
                  className="w-4 h-4 rounded border-neutral-300 text-primary-600 focus:ring-primary-500"
                />
                <span className="text-sm text-neutral-600 dark:text-neutral-400">
                  Select all importable ({subscriptions.filter(s => !s.isImported).length})
                </span>
              </div>

              {/* Subscription List */}
              {subscriptions.map((sub) => (
                <div
                  key={sub.subscriptionId}
                  className={`border rounded-lg p-4 ${
                    sub.isImported
                      ? 'bg-neutral-50 dark:bg-neutral-800/50 border-neutral-200 dark:border-neutral-700 opacity-60'
                      : selectedSubscriptions[sub.subscriptionId]
                      ? 'border-primary-300 dark:border-primary-700 bg-primary-50 dark:bg-primary-900/20'
                      : 'border-neutral-200 dark:border-neutral-700 bg-white dark:bg-neutral-800'
                  }`}
                >
                  <div className="flex items-start gap-4">
                    {/* Checkbox */}
                    <div className="pt-1">
                      {sub.isImported ? (
                        <CheckCircle className="w-5 h-5 text-green-500" />
                      ) : (
                        <input
                          type="checkbox"
                          checked={selectedSubscriptions[sub.subscriptionId] || false}
                          onChange={(e) => handleSelectSubscription(sub.subscriptionId, e.target.checked)}
                          className="w-4 h-4 rounded border-neutral-300 text-primary-600 focus:ring-primary-500"
                        />
                      )}
                    </div>

                    {/* Subscription Info */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-medium text-neutral-900 dark:text-white">
                          {sub.name || `Subscription ${sub.subscriptionId}`}
                        </span>
                        <span className={`px-2 py-0.5 text-xs rounded ${
                          sub.status === 'active'
                            ? 'bg-green-100 text-green-700 dark:bg-green-900/50 dark:text-green-300'
                            : 'bg-neutral-100 text-neutral-600 dark:bg-neutral-700 dark:text-neutral-300'
                        }`}>
                          {sub.status}
                        </span>
                        {sub.isImported && (
                          <span className={`px-2 py-0.5 text-xs rounded ${
                            sub.importedSource === 'portal'
                              ? 'bg-purple-100 text-purple-700 dark:bg-purple-900/50 dark:text-purple-300'
                              : 'bg-blue-100 text-blue-700 dark:bg-blue-900/50 dark:text-blue-300'
                          }`}>
                            {sub.importedSource === 'portal' ? 'Created in Portal' : 'Already Imported'}
                          </span>
                        )}
                      </div>
                      <div className="text-sm text-neutral-600 dark:text-neutral-400 flex flex-wrap gap-x-4 gap-y-1">
                        <span>${parseFloat(sub.amount || 0).toFixed(2)}</span>
                        <span>ID: {sub.subscriptionId}</span>
                        {sub.accountNumber && <span>****{sub.accountNumber.slice(-4)}</span>}
                        {sub.createTimeStampUTC && (
                          <span>Created: {new Date(sub.createTimeStampUTC).toLocaleDateString()}</span>
                        )}
                      </div>
                    </div>

                    {/* Company Assignment */}
                    <div className="w-64">
                      {sub.isImported ? (
                        <div className="text-sm text-neutral-500 dark:text-neutral-400">
                          {sub.importedSource === 'portal' ? 'Created in' : 'Imported to'}: {sub.importedCompanyName || `Company ${sub.importedCompanyId}`}
                        </div>
                      ) : sub.isMatched ? (
                        <div className="flex items-center gap-2 text-sm">
                          <Link2 className="w-4 h-4 text-green-500" />
                          <span className="text-green-700 dark:text-green-300">
                            {sub.matchedCompanyName}
                          </span>
                        </div>
                      ) : (
                        <div className="relative">
                          <input
                            type="text"
                            value={companySearch[sub.subscriptionId] || ''}
                            onChange={(e) => handleCompanySearch(sub.subscriptionId, e.target.value)}
                            onFocus={() => {
                              if (searchResults[sub.subscriptionId]?.length > 0) {
                                setShowCompanyDropdown(prev => ({ ...prev, [sub.subscriptionId]: true }));
                              }
                            }}
                            placeholder="Search company..."
                            className="w-full px-3 py-1.5 text-sm border border-neutral-300 dark:border-neutral-600 rounded-lg bg-white dark:bg-neutral-700"
                          />
                          {showCompanyDropdown[sub.subscriptionId] && searchResults[sub.subscriptionId]?.length > 0 && (
                            <div className="absolute z-10 w-full mt-1 bg-white dark:bg-neutral-800 border border-neutral-200 dark:border-neutral-700 rounded-lg shadow-lg max-h-40 overflow-auto">
                              {searchResults[sub.subscriptionId].map((company) => (
                                <button
                                  key={company.id}
                                  onClick={() => handleAssignCompany(sub.subscriptionId, company)}
                                  className="w-full px-3 py-2 text-left text-sm hover:bg-neutral-100 dark:hover:bg-neutral-700"
                                >
                                  {company.name}
                                </button>
                              ))}
                            </div>
                          )}
                          {companyAssignments[sub.subscriptionId] && (
                            <div className="mt-1 flex items-center gap-1 text-xs text-green-600 dark:text-green-400">
                              <CheckCircle className="w-3 h-3" />
                              Assigned to {companyAssignments[sub.subscriptionId].name}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        {!importProgress?.complete && subscriptions.length > 0 && (
          <div className="px-6 py-4 border-t border-neutral-200 dark:border-neutral-700 flex items-center justify-between">
            <div className="text-sm text-neutral-600 dark:text-neutral-400">
              {selectedCount} selected, {readyToImport} ready to import
            </div>
            <div className="flex gap-3">
              <button
                onClick={onClose}
                className="px-4 py-2 text-neutral-700 dark:text-neutral-300 hover:bg-neutral-100 dark:hover:bg-neutral-700 rounded-lg"
              >
                Cancel
              </button>
              <button
                onClick={handleImport}
                disabled={readyToImport === 0 || importMutation.isPending}
                className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {importMutation.isPending ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Importing...
                  </>
                ) : (
                  <>
                    <Upload className="w-4 h-4" />
                    Import {readyToImport} Subscription{readyToImport !== 1 ? 's' : ''}
                  </>
                )}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
